"use client"
import { debounce } from "lodash"; // Install lodash if not already installed
import "../../../components/chat/ChatPage.css";

// Image cache to prevent duplicate requests
const imageCache = new Map();
const loadingImages = new Set();
// Cached image component
const CachedImage = ({ src, alt, style, ...props }) => {
  const [imageSrc, setImageSrc] = useState(() => {
    // Initialize with placeholder if src is invalid
    if (!src ||
      src === '' ||
      src === 'null' ||
      src === 'undefined' ||
      src === null ||
      src === undefined ||
      typeof src === 'object' ||
      (typeof src === 'string' && (src.trim() === '' || src.trim().toLowerCase() === 'null' || src.trim().toLowerCase() === 'undefined'))) {
      return "/icons/person.png";
    }
    const trimmedSrc = String(src).trim();
    if (trimmedSrc.startsWith('null') || trimmedSrc.startsWith('undefined') || trimmedSrc.includes('null/') || trimmedSrc.includes('undefined/')) {
      return "/icons/person.png";
    }
    return src;
  });
  const [isLoading, setIsLoading] = useState(() => {
    // Blob URLs are immediately available, no loading needed
    if (src && typeof src === 'string' && src.startsWith('blob:')) {
      return false;
    }
    // Only show loading if src is valid and not cached
    const trimmedSrc = src && typeof src === 'string' ? src.trim() : '';
    if (!src || trimmedSrc === '' || trimmedSrc === 'null' || trimmedSrc === 'undefined') {
      return false;
    }
    return !imageCache.has(src);
  });
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    document && document.body && (document.body.style.overflow = "hidden");

    // Handle empty strings, null, undefined, or invalid URLs
    // Also check for string versions of null/undefined
    if (!src ||
      src === '' ||
      src === 'null' ||
      src === 'undefined' ||
      src === null ||
      src === undefined ||
      typeof src === 'object' ||
      (typeof src === 'string' && (src.trim() === '' || src.trim().toLowerCase() === 'null' || src.trim().toLowerCase() === 'undefined'))) {
      setImageSrc("/icons/person.png");
      setIsLoading(false);
      return;
    }

    // Additional validation - check if src looks like a valid URL or path
    const trimmedSrc = String(src).trim();
    if (trimmedSrc.startsWith('null') || trimmedSrc.startsWith('undefined') || trimmedSrc.includes('null/') || trimmedSrc.includes('undefined/')) {
      setImageSrc("/icons/person.png");
      setIsLoading(false);
      return;
    }

    // Handle blob URLs immediately (for uploaded images)
    if (trimmedSrc.startsWith('blob:')) {
      setImageSrc(src);
      setIsLoading(false);
      loadingImages.delete(src);
      return;
    }

    // Check if image is already cached
    if (imageCache.has(src)) {
      setImageSrc(imageCache.get(src));
      setIsLoading(false);
      return;
    }

    // Check if image is currently loading
    if (loadingImages.has(src)) {
      const checkLoading = () => {
        if (imageCache.has(src)) {
          setImageSrc(imageCache.get(src));
          setIsLoading(false);
        } else {
          setTimeout(checkLoading, 50);
        }
      };
      checkLoading();
      return;
    }

    // Start loading the image
    loadingImages.add(src);
    const img = new Image();

    img.onload = () => {
      if (img.width > 0 && img.height > 0) {
        imageCache.set(src, src);
        loadingImages.delete(src);
        setImageSrc(src);
        setIsLoading(false);
      } else {
        // Image failed to load properly
        loadingImages.delete(src);
        setImageSrc("/icons/person.png");
        setIsLoading(false);
        setHasError(true);
      }
    };

    img.onerror = () => {
      loadingImages.delete(src);
      setImageSrc("/icons/person.png");
      setIsLoading(false);
      setHasError(true);
    };

    try {
      // Check if it's a blob URL first (for immediate display of uploaded images)
      if (src.startsWith('blob:')) {
        setImageSrc(src);
        setIsLoading(false);
        loadingImages.delete(src);
        return;
      }

      const url = new URL(src, window.location.origin);
      if (url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'data:' || url.protocol === 'blob:') {
        img.src = src;
      } else {
        // Invalid protocol, use placeholder
        loadingImages.delete(src);
        setImageSrc("/icons/person.png");
        setIsLoading(false);
      }
    } catch (e) {
      // For blob URLs, try to use them directly even if URL parsing fails
      if (src.startsWith('blob:')) {
        setImageSrc(src);
        setIsLoading(false);
        loadingImages.delete(src);
      } else {
        // Invalid URL, use placeholder
        loadingImages.delete(src);
        setImageSrc("/icons/person.png");
        setIsLoading(false);
      }
    }
  }, [src]);

  return (
    <img
      src={imageSrc}
      alt={alt}
      onError={(e) => {
        // Fallback to placeholder if image fails to load
        if (e.target.src !== "/icons/person.png") {
          e.target.src = "/icons/person.png";
        }
      }}
      style={{
        ...style,
        opacity: 1, // Always use full opacity for consistent appearance
        transition: 'opacity 0.2s ease-in-out'
      }}
      {...props}
    />
  );
};

// Audio Waveform Component for real-time recording
const AudioWaveform = ({ audioContext, analyserNode, isRecording, width = 200, height = 40, color = "#4A90E2" }) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    if (!analyserNode || !canvasRef.current || !isRecording) {
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const bufferLength = analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!isRecording || !analyserNode) {
        return;
      }

      animationFrameRef.current = requestAnimationFrame(draw);

      analyserNode.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barCount = 40; // Number of bars in waveform
      const barWidth = (canvas.width - (barCount - 1) * 2) / barCount;
      const barGap = 2;
      const minBarHeight = 2; // Minimum bar height for visual consistency

      for (let i = 0; i < barCount; i++) {
        const dataIndex = Math.floor((i / barCount) * bufferLength);
        const normalizedValue = dataArray[dataIndex] / 255;
        const barHeight = Math.max(minBarHeight, normalizedValue * canvas.height * 0.9);
        const x = i * (barWidth + barGap);
        const y = (canvas.height - barHeight) / 2;

        // Create gradient for visual appeal
        const gradient = ctx.createLinearGradient(0, y, 0, y + barHeight);
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, color + '80');

        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, barWidth, barHeight);
      }
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [analyserNode, isRecording, color]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ display: 'block' }}
    />
  );
};

import { useState, useRef, useEffect, useMemo, useCallback, memo } from "react"
import { useParams } from "react-router-dom"
import { Search, Phone, Video, MoreHorizontal, Paperclip, Camera, Smile, Mic, Send, RefreshCw, Star } from "lucide-react"
import { conversationsAPI, clinicAppointmentsAPI, updateTypingStatus, clinicAPI, urgentCaseAPI, petAPI } from "../../../services/api"
import Navbar from "../../../components/account/Navbar"
import { authUtils } from "../../../services/authAPI"
import { getRTLStyles } from "../../../utils/textDirection"
import { useWebSocket } from "../../../hooks/useWebSocket"
import EmojiPicker from "../../../components/chat/EmojiPicker"
import AppointmentModal from "../../../components/modals/AppointmentModal"
import SelectClinicModal from "../../../components/modals/SelectClinicModal"
import { useClinicData } from "../../../hooks/useClinicData"
import clinicService from "../../../services/clinicService"
import { useTypingIndicator } from "../../../hooks/useTypingIndicator"
import TypingIndicator from "../../../components/chat/TypingIndicator"
import { getEcho } from "../../../services/echo"
import { showSuccessToast, showErrorToast } from "../../../utils/notificationUtils"
import { useTranslation } from "react-i18next"
import RatingModal from "../../../components/modals/RatingModal"
// Add CSS for smooth message animations
const messageAnimationStyles = `
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Helper function to check if URL is from S3 or external domain
// Note: S3 URLs should NOT use crossOrigin if CORS is not configured
const isExternalUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    const urlObj = new URL(url, window.location.origin);
    const currentOrigin = window.location.origin;
    const urlOrigin = urlObj.origin;
    // Check if it's a different origin (but NOT S3, as S3 may not have CORS configured)
    // Only use crossOrigin for CloudFront or other CDNs that have CORS configured
    return urlOrigin !== currentOrigin &&
      !url.includes('amazonaws.com') &&
      !url.includes('s3.');
  } catch {
    return false;
  }
};

// WhatsApp-style Audio Player Component
const WhatsAppAudioPlayer = ({ src, isMe = false, duration = null }) => {
  // DO NOT use crossOrigin for S3 URLs - they don't have CORS configured
  // Only use crossOrigin for CDNs that have CORS properly configured
  const isExternal = isExternalUrl(src);
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const isSetupRef = useRef(false);
  const timeUpdateFrameRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [webAudioAvailable, setWebAudioAvailable] = useState(true); // Track if Web Audio API is available
  const loadingStartTimeRef = useRef(null);

  useEffect(() => {
    // Reset loading state when src changes
    setIsLoading(true);

    if (!src) {
      setIsLoading(false);
      return;
    }
  }, [src]);

  useEffect(() => {
    if (!src) {
      return;
    }

    // Wait for audio element to be in DOM
    const setupAudio = () => {
      if (!audioRef.current) {
        // Retry after a short delay if audio element isn't ready
        setTimeout(setupAudio, 50);
        return;
      }

      const audio = audioRef.current;
      let loadingTimeout = null;

      const handleLoadStart = () => {
        setIsLoading(true);
        loadingStartTimeRef.current = Date.now();
      };

      const finishLoading = () => {
        // Clear any existing timeout
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          loadingTimeout = null;
        }
        // Show loading for minimum 500ms for better UX
        const elapsed = loadingStartTimeRef.current ? Date.now() - loadingStartTimeRef.current : 0;
        const minDisplayTime = 500;
        if (elapsed < minDisplayTime) {
          loadingTimeout = setTimeout(() => {
            setIsLoading(false);
            loadingTimeout = null;
          }, minDisplayTime - elapsed);
        } else {
          setIsLoading(false);
        }
      };

      const handleLoadedMetadata = () => {
        if (audio.duration) {
          setTotalDuration(audio.duration);
        }
        finishLoading();
      };

      const handleCanPlay = () => {
        finishLoading();
      };

      const handleTimeUpdate = () => {
        if (audio.currentTime !== undefined && !isNaN(audio.currentTime)) {
          setCurrentTime(audio.currentTime);
        }
      };

      // Also update time more frequently for smoother progress
      const updateTime = () => {
        if (audio && !audio.paused && !audio.ended) {
          if (audio.currentTime !== undefined && !isNaN(audio.currentTime)) {
            setCurrentTime(audio.currentTime);
          }
          timeUpdateFrameRef.current = requestAnimationFrame(updateTime);
        } else {
          timeUpdateFrameRef.current = null;
        }
      };

      const startTimeUpdateLoop = () => {
        if (!timeUpdateFrameRef.current && audio && !audio.paused && !audio.ended) {
          timeUpdateFrameRef.current = requestAnimationFrame(updateTime);
        }
      };

      const stopTimeUpdateLoop = () => {
        if (timeUpdateFrameRef.current) {
          cancelAnimationFrame(timeUpdateFrameRef.current);
          timeUpdateFrameRef.current = null;
        }
      };

      const handlePlay = () => {
        // Ensure audio is unmuted when playing
        audio.muted = false;
        audio.volume = 1.0;
        setIsPlaying(true);
        // Start the update loop immediately
        startTimeUpdateLoop();
      };

      const handlePlaying = () => {
        // This event fires when audio actually starts playing (after play event)
        // Ensure audio is unmuted when playing
        audio.muted = false;
        audio.volume = 1.0;
        setIsPlaying(true);
        // Ensure update loop is running
        startTimeUpdateLoop();
      };

      const handlePause = () => {
        setIsPlaying(false);
        stopTimeUpdateLoop();
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(audio.duration || 0);
      };

      const handleError = (e) => {
        const error = audio.error;
        console.error('Audio loading error:', {
          code: error?.code,
          message: error?.message,
          src: audio.src,
          readyState: audio.readyState
        });

        // If error is due to CORS or source issues, try removing crossOrigin
        if (error && (error.code === 4 || error.code === 3)) {
          if (audio.crossOrigin) {
            audio.crossOrigin = null;
            audio.load();
          }
        }

        setIsLoading(false);
      };

      // Mark loading start time
      loadingStartTimeRef.current = Date.now();

      // Check if audio is already loaded
      if (audio.readyState >= 2) {
        // HAVE_CURRENT_DATA or higher means metadata is loaded
        if (audio.duration) {
          setTotalDuration(audio.duration);
        }
        // Show loading for minimum 500ms even if already loaded
        loadingTimeout = setTimeout(() => setIsLoading(false), 500);
      } else {
        setIsLoading(true);
      }

      // Attach event listeners
      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('playing', handlePlaying);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);

      // Check initial playing state
      if (!audio.paused) {
        setIsPlaying(true);
      }

      // Set initial playback rate
      audio.playbackRate = playbackRate;

      // Ensure audio is unmuted and volume is set
      audio.muted = false;
      audio.volume = 1.0;

      // Force load if needed (this will trigger loadstart if not already loaded)
      if (audio.readyState < 2) {
        audio.load();
      }

      // Set unmuted again after load
      setTimeout(() => {
        if (audio) {
          audio.muted = false;
          audio.volume = 1.0;
        }
      }, 100);

      // Fallback: ensure loading state clears after 5 seconds max
      const fallbackTimeout = setTimeout(() => {
        setIsLoading(false);
      }, 5000);

      return () => {
        if (fallbackTimeout) {
          clearTimeout(fallbackTimeout);
        }
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
        }
        stopTimeUpdateLoop();
        // Remove all event listeners
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('playing', handlePlaying);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
      };
    };

    const cleanup = setupAudio();
    return cleanup || (() => { });
  }, [src, playbackRate]);

  // Update playback rate
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Setup waveform visualization (optional - only if Web Audio API is available)
  useEffect(() => {
    if (!audioRef.current || !canvasRef.current || isLoading) return;

    const audio = audioRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // If Web Audio API is not available, draw a static waveform
    if (!webAudioAvailable) {
      // Draw a simple static waveform pattern
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const barCount = 40;
      const barWidth = (canvas.width - (barCount - 1) * 2) / barCount;
      const barGap = 2;
      const color = '#EF4444';

      // Draw static bars with varying heights
      for (let i = 0; i < barCount; i++) {
        const barHeight = 4 + Math.random() * (canvas.height * 0.6);
        const x = i * (barWidth + barGap);
        const y = (canvas.height - barHeight) / 2;

        ctx.fillStyle = color;
        ctx.fillRect(x, y, barWidth, barHeight);
      }
      return; // Exit early - no need to setup Web Audio API
    }

    // Reset audio context when src changes
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => { });
      audioContextRef.current = null;
      sourceRef.current = null;
      analyserRef.current = null;
      isSetupRef.current = false;
    }

    const setupAudioContext = async () => {
      if (isSetupRef.current || audioContextRef.current) return;

      // If Web Audio API is not available, skip setup and allow direct playback
      if (!webAudioAvailable) {
        return;
      }

      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtx.state === 'suspended') {
          try {
            await audioCtx.resume();
          } catch (resumeError) {
            console.warn('WhatsAppAudioPlayer: Unable to resume audio context:', resumeError);
          }
        }
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;

        if (!sourceRef.current) {
          // If this fails, audio won't play. So we need to be very careful here.
          // Check if audio is ready and can play before attempting this
          if (audio.readyState < 2) {
            console.warn('Audio not ready for Web Audio API setup, will retry when ready');
            return;
          }

          try {
            const source = audioCtx.createMediaElementSource(audio);
            source.connect(analyser);
            analyser.connect(audioCtx.destination);
            sourceRef.current = source;
          } catch (sourceError) {
            // but allow audio to play directly
            console.warn('Failed to create MediaElementSource (likely CORS issue). Audio will play directly without waveform:', sourceError);
            setWebAudioAvailable(false);
            audioContextRef.current?.close().catch(() => { });
            audioContextRef.current = null;
            return; // Exit early - audio can still play directly
          }
        }

        audioContextRef.current = audioCtx;
        analyserRef.current = analyser;
        isSetupRef.current = true;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
          if (!analyserRef.current || !canvasRef.current || !webAudioAvailable) return;

          if (audio.paused || audio.ended) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            animationFrameRef.current = requestAnimationFrame(draw);
            return;
          }

          animationFrameRef.current = requestAnimationFrame(draw);
          analyserRef.current.getByteFrequencyData(dataArray);
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          const barCount = 40;
          const barWidth = (canvas.width - (barCount - 1) * 2) / barCount;
          const barGap = 2;
          const minBarHeight = 2;
          const color = '#EF4444';

          for (let i = 0; i < barCount; i++) {
            const dataIndex = Math.floor((i / barCount) * bufferLength);
            const normalizedValue = dataArray[dataIndex] / 255;
            const barHeight = Math.max(minBarHeight, normalizedValue * canvas.height * 0.8);
            const x = i * (barWidth + barGap);
            const y = (canvas.height - barHeight) / 2;

            const gradient = ctx.createLinearGradient(0, y, 0, y + barHeight);
            gradient.addColorStop(0, color);
            gradient.addColorStop(1, color + '80');

            ctx.fillStyle = gradient;
            ctx.fillRect(x, y, barWidth, barHeight);
          }
        };

        draw();
      } catch (error) {
        // If Web Audio API setup fails completely, disable it but allow direct playback
        console.warn('Web Audio API setup failed. Audio will play directly without waveform visualization:', error);
        setWebAudioAvailable(false);
        // Ensure audio can still play directly
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => { });
          audioContextRef.current = null;
        }
        sourceRef.current = null;
        analyserRef.current = null;
        isSetupRef.current = false;
      }
    };

    const handlePlay = () => {
      // Only attempt Web Audio API setup if it's available and not already set up
      if (webAudioAvailable && !isSetupRef.current) {
        setupAudioContext();
      }
    };

    audio.addEventListener('play', handlePlay);

    // Only setup if audio is already playing and Web Audio API is available
    if (!audio.paused && webAudioAvailable) {
      setupAudioContext();
    }

    return () => {
      audio.removeEventListener('play', handlePlay);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => { });
        audioContextRef.current = null;
      }
      sourceRef.current = null;
      analyserRef.current = null;
      isSetupRef.current = false;
    };
  }, [isLoading, src, webAudioAvailable]);

  // Draw static waveform when Web Audio API is not available and audio is playing
  useEffect(() => {
    if (!canvasRef.current || !audioRef.current || webAudioAvailable) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const audio = audioRef.current;

    // Only draw static waveform when playing
    if (!audio.paused && !audio.ended) {
      const drawStatic = () => {
        if (audio.paused || audio.ended || webAudioAvailable) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const barCount = 40;
        const barWidth = (canvas.width - (barCount - 1) * 2) / barCount;
        const barGap = 2;
        const color = '#EF4444';

        // Draw animated static bars
        for (let i = 0; i < barCount; i++) {
          const barHeight = 4 + Math.random() * (canvas.height * 0.6);
          const x = i * (barWidth + barGap);
          const y = (canvas.height - barHeight) / 2;

          ctx.fillStyle = color;
          ctx.fillRect(x, y, barWidth, barHeight);
        }

        if (!audio.paused && !audio.ended) {
          requestAnimationFrame(drawStatic);
        }
      };

      drawStatic();
    } else {
      // Clear canvas when paused
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [isPlaying, webAudioAvailable]);

  // Format time helper
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    if (!audioRef.current) {
      console.warn('Audio element not available');
      return;
    }

    const audio = audioRef.current;

    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch((resumeError) => {
        console.warn('WhatsAppAudioPlayer: Audio context resume failed:', resumeError);
      });
    }

    // Check if audio has valid sources
    if (!audio.src || audio.readyState === 0) {
      console.warn('Audio has no valid source or not loaded yet. Attempting to load...');
      // Try to reload the audio
      if (src) {
        audio.load();
        // Wait a bit for audio to load, then try playing
        setTimeout(() => {
          if (audio.readyState >= 2) {
            togglePlayPause();
          }
        }, 500);
      }
      return;
    }

    // Check actual audio state, not React state
    if (audio.paused) {
      // Audio is paused, so play it
      // Ensure audio is unmuted before playing
      audio.muted = false;
      audio.volume = 1.0;


      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Ensure still unmuted after play
            audio.muted = false;
            audio.volume = 1.0;
            setIsPlaying(true);

            // Start time update loop immediately
            if (audio && !audio.paused && !audio.ended) {
              const updateTime = () => {
                if (audio && !audio.paused && !audio.ended) {
                  if (audio.currentTime !== undefined && !isNaN(audio.currentTime)) {
                    setCurrentTime(audio.currentTime);
                  }
                  timeUpdateFrameRef.current = requestAnimationFrame(updateTime);
                } else {
                  timeUpdateFrameRef.current = null;
                }
              };
              if (!timeUpdateFrameRef.current) {
                timeUpdateFrameRef.current = requestAnimationFrame(updateTime);
              }
            }
          })
          .catch(err => {
            console.error('Audio play error:', {
              name: err.name,
              message: err.message,
              stack: err.stack,
              audioSrc: audio.src,
              readyState: audio.readyState,
              networkState: audio.networkState,
              error: audio.error
            });
            setIsPlaying(false);

            // Handle different error types
            if (err.name === 'NotAllowedError') {
              console.warn('Browser autoplay policy blocked playback. User interaction required.');
              // This is usually fine - user can click again
            } else if (err.name === 'NotSupportedError') {
              console.warn('Audio format not supported or source issue');
              // Try removing crossOrigin if present
              if (audio.crossOrigin) {
                audio.crossOrigin = null;
                audio.load();
                // Retry after load
                setTimeout(() => {
                  if (audio.readyState >= 2) {
                    togglePlayPause();
                  }
                }, 500);
              }
            } else if (err.name === 'AbortError') {
              console.warn('Audio playback was aborted');
            } else {
              console.error('Unknown audio playback error:', err);
            }
          });
      } else {
        // Fallback for browsers that don't return a promise
        setIsPlaying(true);
      }
    } else {
      // Audio is playing, so pause it
      audio.pause();
      setIsPlaying(false);
    }
  };

  // Handle playback speed change
  const handleSpeedChange = (speed) => {
    setPlaybackRate(speed);
  };

  // Handle seeking when user clicks on waveform
  const handleWaveformClick = (e) => {
    if (!audioRef.current || !displayDuration || displayDuration <= 0) return;

    const waveformContainer = e.currentTarget;
    const rect = waveformContainer.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * displayDuration;

    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Use provided duration or calculated duration
  const displayDuration = duration || totalDuration;

  // Loading state - show recording-indicator-style UI
  if (isLoading) {
    return (
      <div ref={containerRef} className={`whatsapp-audio-player loading ${isMe ? 'is-me' : ''}`}>
        <div className="whatsapp-audio-loading-content">
          <div className="whatsapp-audio-loading-animation">
            <div className="whatsapp-audio-loading-dot"></div>
          </div>
          <div className="whatsapp-audio-loading-waveform">
            <div className="whatsapp-audio-loading-waveform-placeholder">
              {/* Static waveform bars for loading state */}
              <div className="whatsapp-audio-loading-bars">
                {Array.from({ length: 20 }).map((_, i) => {
                  // Fixed heights for consistent appearance, with slight variation
                  const heights = [30, 50, 70, 40, 60, 45, 55, 35, 65, 50, 40, 60, 45, 55, 50, 35, 65, 40, 60, 50];
                  return (
                    <div
                      key={i}
                      className="whatsapp-audio-loading-bar"
                      style={{
                        height: `${heights[i % heights.length]}%`,
                        animationDelay: `${i * 0.1}s`
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
          <div className="whatsapp-audio-loading-info">
            <span className="whatsapp-audio-loading-text">{t("chat.common.loading")}</span>
            {displayDuration > 0 && (
              <span className="whatsapp-audio-loading-duration">{formatTime(displayDuration)}</span>
            )}
          </div>
        </div>
        <audio
          crossOrigin="anonymous"
          ref={audioRef}
          src={src}
          preload="metadata"
          style={{ display: 'none' }}
          onLoadedMetadata={(e) => {
            const audio = e.target;
            audio.muted = false;
            audio.volume = 1.0;
          }}
          onCanPlay={(e) => {
            const audio = e.target;
            audio.muted = false;
            audio.volume = 1.0;
          }}
          onError={(e) => {
            const audio = e.target;
            console.error('Audio loading error:', {
              code: audio.error?.code,
              message: audio.error?.message,
              src: audio.src
            });
          }}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`whatsapp-audio-player loaded ${isMe ? 'is-me' : ''}`}>
      <div className="whatsapp-audio-content">
        <button
          className="whatsapp-audio-play-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            togglePlayPause();
          }}
          disabled={isLoading}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="4" y="2" width="3" height="12" />
              <rect x="9" y="2" width="3" height="12" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 2l10 6-10 6V2z" />
            </svg>
          )}
        </button>

        <div
          className="whatsapp-audio-waveform"
          onClick={handleWaveformClick}
          style={{ cursor: 'pointer' }}
          title="Click to seek"
        >
          <audio
            crossOrigin="anonymous"
            ref={audioRef}
            src={src}
            preload="metadata"
            style={{ display: 'none' }}
            onLoadedMetadata={(e) => {
              const audio = e.target;
              audio.muted = false;
              audio.volume = 1.0;
            }}
            onCanPlay={(e) => {
              const audio = e.target;
              audio.muted = false;
              audio.volume = 1.0;
            }}
            onError={(e) => {
              const audio = e.target;
              console.error('Audio loading error:', {
                code: audio.error?.code,
                message: audio.error?.message,
                src: audio.src
              });
            }}
          />
          <canvas
            ref={canvasRef}
            width={200}
            height={40}
            className="whatsapp-audio-waveform-canvas"
          />
          <div className="whatsapp-audio-waveform-progress">
            <div
              className="whatsapp-audio-waveform-progress-bar"
              style={{ width: `${displayDuration > 0 ? (currentTime / displayDuration) * 100 : 0}%` }}
            />
          </div>
        </div>

        <div className="whatsapp-audio-info">
          <span className="whatsapp-audio-duration">{formatTime(currentTime)} / {formatTime(displayDuration)}</span>
        </div>

        <div className="whatsapp-audio-speed-selector">
          <button
            className={`whatsapp-audio-speed-btn ${playbackRate === 0.5 ? 'active' : ''}`}
            onClick={() => handleSpeedChange(0.5)}
            title="0.5x speed"
          >
            0.5x
          </button>
          <button
            className={`whatsapp-audio-speed-btn ${playbackRate === 1 ? 'active' : ''}`}
            onClick={() => handleSpeedChange(1)}
            title="1x speed"
          >
            1x
          </button>
          <button
            className={`whatsapp-audio-speed-btn ${playbackRate === 1.5 ? 'active' : ''}`}
            onClick={() => handleSpeedChange(1.5)}
            title="1.5x speed"
          >
            1.5x
          </button>
          <button
            className={`whatsapp-audio-speed-btn ${playbackRate === 2 ? 'active' : ''}`}
            onClick={() => handleSpeedChange(2)}
            title="2x speed"
          >
            2x
          </button>
        </div>
      </div>
    </div>
  );
};

// MessageBubble component with memoization
const MessageBubble = memo(({ message, currentChat, openMediaModal, getFileIcon }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user_data") || "{}");
    } catch (e) {
      return {};
    }
  }, []);

  // Strictly check if message is from current user
  // Primary check: isOwnMessage flag (most reliable, from API)
  // If isOwnMessage is explicitly false, it's NOT from current user
  // Fallback: check senderId === "me" or compare user IDs
  let isMe = false;

  if (message.isOwnMessage === true) {
    isMe = true;
  } else if (message.isOwnMessage === false) {
    isMe = false;
  } else {
    // isOwnMessage is undefined/null - use fallback checks
    if (message.senderId === "me") {
      isMe = true;
    } else if (userData && message.user) {
      // Compare user IDs as final fallback
      const currentUserId = userData.user_id || userData.id;
      const messageUserId = message.user.user_id || message.user.id;
      if (currentUserId && messageUserId) {
        isMe = String(currentUserId) === String(messageUserId);
      }
    }
  }

  const isSystemMessage = message.isSystemMessage || message.senderId === "system";

  // No observer needed - always show media directly
  const [videoLoading, setVideoLoading] = useState({}); // Track which videos are loading
  const [videoLoaded, setVideoLoaded] = useState({}); // Track which videos have been loaded

  return (
    <div
      id={message.id || message.messageId}
      className={`message-bubble-container ${isMe ? 'is-me' : ''} ${message.isDeleting ? 'is-deleting' : ''}`}
      style={{
        display: "flex",
        width: "100%",
        direction: "ltr",
        justifyContent: isMe ? "flex-end" : "flex-start",
        marginBottom: "20px",
        alignItems: "flex-end",
        animation: "slideInUp 0.3s ease-out",
      }}
    >
      {!isMe && (() => {
        // Normalize and validate profile image URL
        let profileImageSrc = message.user?.profile_image_url;
        if (typeof profileImageSrc === 'string') {
          profileImageSrc = profileImageSrc.trim();
          if (profileImageSrc === '' ||
            profileImageSrc.toLowerCase() === 'null' ||
            profileImageSrc.toLowerCase() === 'undefined' ||
            profileImageSrc.startsWith('null') ||
            profileImageSrc.startsWith('undefined') ||
            profileImageSrc.includes('null/') ||
            profileImageSrc.includes('undefined/')) {
            profileImageSrc = null;
          }
        } else {
          profileImageSrc = null;
        }

        // Try currentChat avatar as fallback
        if (!profileImageSrc && currentChat?.avatar) {
          let chatAvatar = currentChat.avatar;
          if (typeof chatAvatar === 'string') {
            chatAvatar = chatAvatar.trim();
            if (chatAvatar !== '' &&
              chatAvatar.toLowerCase() !== 'null' &&
              chatAvatar.toLowerCase() !== 'undefined' &&
              !chatAvatar.startsWith('null') &&
              !chatAvatar.startsWith('undefined')) {
              profileImageSrc = chatAvatar;
            }
          }
        }

        // Final fallback to placeholder
        if (!profileImageSrc) {
          profileImageSrc = "/icons/person.png";
        }

        return (
          <CachedImage
            src={profileImageSrc}
            alt={message.senderName}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              objectFit: "cover",
              marginRight: "8px",
              marginBottom: "20px",
            }}
          />
        );
      })()}

      <div
        className="message-bubble-wrapper"
        style={{
          maxWidth: "70%",
          display: "flex",
          flexDirection: "column",
          alignItems: isMe ? "flex-end" : "flex-start"
        }}
      >
        {!isMe && (
          <div style={{
            fontSize: "12px",
            color: "#666666",
            marginBottom: "4px",
            marginLeft: "4px",
            fontWeight: "500"
          }}>
            {message.senderName || message.user?.first_name || message.user?.name || currentChat?.name || t('chat.list.unknownUser')}
          </div>
        )}
        {isMe && (
          <div style={{
            fontSize: "12px",
            color: "#666666",
            marginBottom: "4px",
            marginRight: "4px",
            textAlign: "right",
            fontWeight: "500"
          }}>
            {t('chat.messages.you')}
          </div>
        )}
        <div
          className={`message-bubble ${isMe ? 'is-me' : ''}`}
          style={{
            padding: "12px 16px",
            borderRadius: "20px",
            backgroundColor: (message.media && message.media.length > 0) ? "transparent" : (isMe ? "#4A90E2" : "#E8E8E8"),
            color: isMe ? "#FFFFFF" : "#333333",
            marginBottom: "4px",
            wordWrap: "break-word",
          }}
        >
          {message.type === "text" && (
            <p style={{
              fontSize: "14px",
              lineHeight: "1.4",
              margin: 0,
              ...getRTLStyles(message.content)
            }}>
              {message.content}
            </p>
          )}
          {message.type !== "text" && (
            <div>
              {message.content && (
                <p style={{
                  fontSize: "14px",
                  lineHeight: "1.4",
                  margin: "0 0 8px 0",
                  ...getRTLStyles(message.content)
                }}>
                  {message.content}
                </p>
              )}
              {message.media && message.media.length > 0 && message.type !== "audio" && !message.media.some(m => m.isAudio || (m.type && m.type.startsWith('audio/'))) && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: message.media.length === 1 ? "1fr" :
                      message.media.length === 2 ? "1fr 1fr" :
                        message.media.length === 3 ? "1fr 1fr" : "1fr 1fr",
                    gap: "4px",
                    marginTop: "8px",
                    maxWidth: "300px",
                  }}
                >
                  {message.media.map((media, index) => (
                    <div
                      key={`${media.url}-${index}`}
                      style={{
                        position: "relative",
                        cursor: "pointer",
                        borderRadius: "8px",
                        overflow: "hidden",
                        aspectRatio: "1",
                        backgroundColor: "#f0f0f0",
                        minHeight: "100px",
                        minWidth: "100px",
                        maxHeight: "150px",
                        maxWidth: "150px",
                        transition: "opacity 0.3s ease"
                      }}
                      onClick={() => openMediaModal(media)}
                    >
                      {media.isImage ? (
                        <div style={{ position: "relative", width: "100%", height: "100%" }}>
                          <CachedImage
                            src={media.url}
                            alt={media.name || "Shared image"}
                            loading="lazy"
                            decoding="async"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              maxWidth: "150px",
                              maxHeight: "150px",
                              opacity: 1,
                              transition: "opacity 0.3s ease",
                            }}
                            onLoad={(e) => {
                              e.target.style.opacity = "1";
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.parentElement.innerHTML = `
                                <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; padding: 8px; background-color: rgba(0, 0, 0, 0.1);">
                                  <span style="font-size: 10px; color: #DC3545;">Failed to load image</span>
                                </div>
                              `;
                            }}
                          />
                        </div>
                      ) : media.isVideo ? (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            position: "relative",
                            backgroundColor: "#000",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onClick={() => {
                            const videoId = `${message.id}-${index}`;
                            if (!videoLoaded[videoId] && !videoLoading[videoId]) {
                              setVideoLoading(prev => ({ ...prev, [videoId]: true }));
                              setTimeout(() => {
                                setVideoLoaded(prev => ({ ...prev, [videoId]: true }));
                                setVideoLoading(prev => ({ ...prev, [videoId]: false }));
                              }, 100);
                            }
                          }}
                        >
                          {videoLoaded[`${message.id}-${index}`] ? (
                            <video
                              controls
                              autoPlay
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                maxWidth: "150px",
                                maxHeight: "150px",
                              }}
                              onLoadedData={() => {
                                const videoId = `${message.id}-${index}`;
                                setVideoLoaded(prev => ({ ...prev, [videoId]: true }));
                                setVideoLoading(prev => ({ ...prev, [videoId]: false }));
                              }}
                              onError={() => {
                                const videoId = `${message.id}-${index}`;
                                setVideoLoading(prev => ({ ...prev, [videoId]: false }));
                              }}
                            >
                              <source src={media.url} type={media.type} />
                            </video>
                          ) : videoLoading[`${message.id}-${index}`] ? (
                            <div style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                            }}>
                              <div style={{
                                width: "20px",
                                height: "20px",
                                border: "2px solid #ffffff",
                                borderTop: "2px solid transparent",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                                marginBottom: "8px"
                              }} />
                              <span style={{ fontSize: "10px" }}>{t("chat.common.loading")}</span>
                            </div>
                          ) : (
                            <div style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                            }}>
                              <div style={{
                                width: "40px",
                                height: "40px",
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginBottom: "8px",
                                transition: "background-color 0.2s ease"
                              }}>
                                <span style={{ fontSize: "20px", marginLeft: "2px" }}>▶️</span>
                              </div>
                              <span style={{ fontSize: "10px", fontWeight: "500" }}>Click to play</span>
                            </div>
                          )}
                        </div>
                      ) : (media.type && media.type.startsWith('audio/')) || media.isAudio ? (
                        <div style={{
                          width: "100%",
                          padding: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>
                          <WhatsAppAudioPlayer
                            src={media.url}
                            isMe={isMe}
                            duration={media.duration}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            padding: "8px",
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            maxWidth: "150px",
                            maxHeight: "150px",
                          }}
                        >
                          <span style={{ fontSize: "24px", marginBottom: "4px" }}>
                            {getFileIcon(media.type)}
                          </span>
                          <p
                            style={{
                              fontSize: "10px",
                              margin: 0,
                              textAlign: "center",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                          >
                            {media.name || 'Unknown file'}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {(message.type === "audio" || (message.media && message.media.some(m => m.isAudio || (m.type && m.type.startsWith('audio/'))))) && (
            <div style={{
              marginTop: message.content ? "8px" : "0",
              width: "100%",
              maxWidth: "300px"
            }}>
              {message.media && message.media.length > 0 ? (
                <div>
                  <WhatsAppAudioPlayer
                    src={message.media[0].url}
                    isMe={isMe}
                    duration={message.duration || message.media[0]?.duration}
                  />
                </div>
              ) : (
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  minWidth: "200px",
                  padding: "4px 0",
                }}>
                  <button
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <Mic style={{ width: "12px", height: "12px", color: "white" }} />
                  </button>
                  <div style={{ flex: 1, height: "2px", backgroundColor: "rgba(255, 255, 255, 0.3)", borderRadius: "1px" }}>
                    <div style={{ height: "100%", width: "40%", backgroundColor: "white", borderRadius: "1px" }} />
                  </div>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "white",
                    }}
                  >
                    {message.duration || '0:00'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            fontSize: "12px",
            color: "#999999",
            textAlign: isMe ? "right" : "left",
            display: "flex",
            alignItems: "center",
            justifyContent: isMe ? "flex-end" : "flex-start",
            gap: "4px",
            width: "100%",
            padding: isMe ? "0 4px 0 0" : "0 0 0 4px"
          }}
        >
          <span>{message.timestamp}</span>
          {isMe && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "2px",
              }}
            >
              {message.isSending ? (
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    border: "2px solid #999999",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : (
                <span
                  style={{
                    fontSize: "12px",
                    color: message.isRead ? "#4CAF50" : "#999999",
                  }}
                >
                  ✓
                </span>
              )}
              {message.isRead && !message.isSending && (
                <span
                  style={{
                    fontSize: "10px",
                    color: "#4CAF50",
                    fontWeight: "500",
                  }}
                >
                  Read
                </span>
              )}
              {message.isSending && (
                <span
                  style={{
                    fontSize: "10px",
                    color: "#999999",
                    fontWeight: "500",
                  }}
                >
                  Sending...
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {isMe && (() => {
        // Sent messages avatar logic
        let profileImageSrc = null;
        try {
          const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
          profileImageSrc = userData.profile_image_url || userData.profileImage;
        } catch (e) { }

        if (!profileImageSrc) {
          profileImageSrc = "/icons/person.png";
        }

        return (
          <CachedImage
            src={profileImageSrc}
            alt="Me"
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              objectFit: "cover",
              marginLeft: "8px",
              marginBottom: "20px",
            }}
          />
        );
      })()}
    </div>
  );
}, (prevProps, nextProps) => {
  const prev = prevProps.message;
  const next = nextProps.message;
  return (
    prev.id === next.id &&
    prev.content === next.content &&
    prev.timestamp === next.timestamp &&
    prev.isRead === next.isRead &&
    prev.senderId === next.senderId &&
    prev.senderName === next.senderName &&
    prev.isOwnMessage === next.isOwnMessage &&
    prev.isSending === next.isSending &&
    prev.user?.profile_image_url === next.user?.profile_image_url &&
    JSON.stringify(prev.media) === JSON.stringify(next.media) &&
    prev.isDeleting === next.isDeleting
  );
});


const DoctorChat = ({ initialChatId = null, onBack = null }) => {
  // Get route parameters
  const { conversationType, conversationId, receiverId } = useParams();

  // Initialize WebSocket connection
  const { isConnected, subscribe, unsubscribe, listen, stopListening } = useWebSocket();

  // Inject CSS animation styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = messageAnimationStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [selectedChat, setSelectedChat] = useState(null)
  const [isRecording, setIsRecording] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState(null)
  const [audioChunks, setAudioChunks] = useState([])
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [recordedAudio, setRecordedAudio] = useState(null)
  const [recordingTimer, setRecordingTimer] = useState(null)
  const [audioContext, setAudioContext] = useState(null)
  const [analyserNode, setAnalyserNode] = useState(null)
  const [audioStream, setAudioStream] = useState(null)
  const [waveformData, setWaveformData] = useState([])
  const [chats, setChats] = useState([])
  const [chatUpdates, setChatUpdates] = useState({}) // Separate state for chat updates
  const [recentlyUpdatedChats, setRecentlyUpdatedChats] = useState(new Set()) // Track chats that were recently updated for animation
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [messages, setMessages] = useState({})
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesError, setMessagesError] = useState(null)

  // Ref to track processed messages to prevent duplicates
  const processedMessages = useRef(new Set())
  const [selectedFiles, setSelectedFiles] = useState([])
  const [isSending, setIsSending] = useState(false)
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState({})
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [showMediaModal, setShowMediaModal] = useState(false)
  const [showEndConversationModal, setShowEndConversationModal] = useState(false)
  const [newMessages, setNewMessages] = useState({}) // Separate state for new messages
  const [messageInputText, setMessageInputText] = useState("") // Track message input for RTL detection
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const [cameraStream, setCameraStream] = useState(null)
  const [cameraMode, setCameraMode] = useState('photo') // 'photo' or 'video'
  const [isRecordingVideo, setIsRecordingVideo] = useState(false)
  const [videoRecorder, setVideoRecorder] = useState(null)
  const [videoChunks, setVideoChunks] = useState([])
  const [cameraLoading, setCameraLoading] = useState(false)
  const [showChatMenu, setShowChatMenu] = useState(false) // New state for chat menu
  const chatMenuRef = useRef(null) // Ref for chat menu dropdown
  const [showRatingModal, setShowRatingModal] = useState(false)
  const [ratingCaseId, setRatingCaseId] = useState(null)

  // Close chat menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatMenuRef.current && !chatMenuRef.current.contains(event.target)) {
        setShowChatMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Appointment modal state
  const [showAppointmentModal, setShowAppointmentModal] = useState(false)
  const [showSelectClinicModal, setShowSelectClinicModal] = useState(false)
  const [isSavingAppointment, setIsSavingAppointment] = useState(false)
  const [targetClinicData, setTargetClinicData] = useState(null)

  // Get clinic data for appointments
  const { data: clinicData, clinicMembers, clinic, ownedClinics } = useClinicData()
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'

  // Get Echo instance for typing indicators
  const echo = getEcho()

  // Initialize typing indicator for the selected conversation
  const { usersTyping: wsUsersTyping, onTyping } = useTypingIndicator(selectedChat, echo)

  // Track typing users from notifications per conversation (fallback when WebSocket fails)
  // Maps conversationId -> Set of user IDs who are typing
  const [conversationTypingUsers, setConversationTypingUsers] = useState({})
  const typingTimeoutsRef = useRef({}) // Track timeouts for auto-removal (keys: conversationId_userId)

  // Helper function to get typing users for a specific conversation
  const getTypingUsersForConversation = useCallback((conversationId) => {
    if (!conversationId) return [];
    const typingSet = conversationTypingUsers[conversationId.toString()];
    return typingSet ? Array.from(typingSet) : [];
  }, [conversationTypingUsers]);

  // Function to stop typing indicator for a specific user in a conversation
  const stopTypingForUser = useCallback((conversationId, userId) => {
    if (!conversationId || !userId) return;

    const convIdStr = conversationId.toString();
    const timeoutKey = `${convIdStr}_${userId}`;

    // Remove from notification-based typing for this conversation
    setConversationTypingUsers(prev => {
      const updated = { ...prev };
      if (updated[convIdStr]) {
        const newSet = new Set(updated[convIdStr]);
        newSet.delete(userId);
        if (newSet.size === 0) {
          delete updated[convIdStr];
        } else {
          updated[convIdStr] = newSet;
        }
      }
      return updated;
    });

    // Clear timeout if exists
    if (typingTimeoutsRef.current[timeoutKey]) {
      clearTimeout(typingTimeoutsRef.current[timeoutKey]);
      delete typingTimeoutsRef.current[timeoutKey];
    }
  }, []);

  // Function to stop typing for current user (when sending message or input is empty)
  const stopCurrentUserTyping = useCallback(async () => {
    if (!selectedChat) return;

    try {
      // Stop typing via API
      await updateTypingStatus(selectedChat, false);
    } catch (error) {
      console.error('❌ Failed to stop typing:', error);
    }
  }, [selectedChat]);

  // Merge WebSocket and notification-based typing indicators for selected conversation only
  const usersTyping = useMemo(() => {
    if (!selectedChat) return [];
    const notificationTyping = conversationTypingUsers[selectedChat.toString()] || new Set();
    const merged = new Set([...wsUsersTyping, ...Array.from(notificationTyping)]);
    return Array.from(merged);
  }, [wsUsersTyping, conversationTypingUsers, selectedChat]);

  // Get selected chat data with updates applied
  const selectedChatData = selectedChat ? (() => {
    const baseChat = chats.find(chat => chat.id === selectedChat);
    const updates = chatUpdates[selectedChat];
    return baseChat && updates ? { ...baseChat, ...updates } : baseChat;
  })() : null
  const [isUserAtBottom, setIsUserAtBottom] = useState(true)
  const [activeTab, setActiveTab] = useState('medical')
  const messagesEndRef = useRef(null)
  const messagesStartRef = useRef(null)
  const fileInputRef = useRef(null)
  const scrollPositionRef = useRef(null)
  const isAddingNewMessageRef = useRef(false)
  const messagesContainerRef = useRef(null)
  const emojiButtonRef = useRef(null)
  const cameraVideoRef = useRef(null)
  const cameraCanvasRef = useRef(null)

  // Watch for empty input and stop typing (safety net)
  // Note: onTyping handler also handles this, but this ensures it stops even if hook fails
  const wasTypingRef = useRef(false);
  useEffect(() => {
    const isEmpty = !messageInputText.trim();
    if (isEmpty && selectedChat && wasTypingRef.current) {
      // Input became empty and we were typing, stop typing
      stopCurrentUserTyping();
      wasTypingRef.current = false;
    } else if (!isEmpty) {
      // Input has content, mark as typing
      wasTypingRef.current = true;
    }
  }, [messageInputText, selectedChat, stopCurrentUserTyping]);

  // Transform API response to match expected format
  const transformChatData = (apiChat) => {
    // Validate required fields
    if (!apiChat || !apiChat.other_participant) {
      console.warn('Invalid chat data received:', apiChat)
      return null
    }

    const otherParticipant = apiChat.other_participant
    const latestMessage = apiChat.latest_message

    // Ensure other_participant has required fields
    if (!otherParticipant.first_name) {
      console.warn('Invalid participant data:', otherParticipant)
      return null
    }

    return {
      id: apiChat.id,
      name: `${otherParticipant.first_name}${otherParticipant.last_name ? ` ${otherParticipant.last_name}` : ''}`,
      avatar: otherParticipant.profile_image_url || "/images/default.jpg",
      lastMessage: latestMessage ? latestMessage.content : t("chat.list.noMessages"),
      isempty: latestMessage ? false : true,
      timestamp: latestMessage ? formatTimestamp(latestMessage.created_at) : formatTimestamp(apiChat.updated_at),
      unreadCount: apiChat.unread_count || 0,
      isOnline: false, // API doesn't provide online status, default to false
      userType: "user", // Default user type
      type: apiChat.type || "all", // Preserve conversation type from API
      otherParticipant: otherParticipant,
      closedAt: apiChat.closed_at,
      closedBy: apiChat.closed_by,
      isClosed: apiChat.closed_at !== null,
      latestMessage: apiChat.latest_message,
      unreadCount: apiChat.unread_count,
      participantsCount: apiChat.participants_count,
      createdAt: apiChat.created_at,
      updatedAt: apiChat.updated_at,
      can_send_message: apiChat.can_send_message !== undefined ? apiChat.can_send_message : true, // Add permission flag
    }
  }

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp)
      const now = new Date()
      const diffInMinutes = Math.floor((now - date) / (1000 * 60))

      if (diffInMinutes < 1) return t("chat.time.justNow", { defaultValue: "Just now" })
      if (diffInMinutes < 60) return t("chat.time.minutesAgo", { count: diffInMinutes, defaultValue: "{{count}}m ago" })

      const diffInHours = Math.floor(diffInMinutes / 60)
      if (diffInHours < 24) return t("chat.time.hoursAgo", { count: diffInHours, defaultValue: "{{count}}h ago" })

      const diffInDays = Math.floor(diffInHours / 24)
      if (diffInDays < 7) return t("chat.time.daysAgo", { count: diffInDays, defaultValue: "{{count}}d ago" })

      return date.toLocaleDateString()
    } catch (error) {
      console.error('Error formatting timestamp:', error)
      return t("chat.time.unknown", { defaultValue: "Unknown" })
    }
  }

  // Memoized chats that combine base chats with updates to prevent unnecessary re-renders
  const memoizedChats = useMemo(() => {
    return chats.map(chat => {
      const update = chatUpdates[chat.id];
      if (update) {
        return { ...chat, ...update };
      }
      return chat;
    });
  }, [chats, chatUpdates]);

  const scrollToBottomSmooth = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const markConversationAsRead = async (conversationId) => {
    try {
      // Check if there are unread messages before calling the API
      const currentChat = memoizedChats.find(chat => chat.id === conversationId)
      const currentMessages = messages[conversationId] || []

      // Only call API if there are unread messages
      if (currentChat?.unreadCount > 0 || currentMessages.some(msg => !msg.isRead)) {
        await conversationsAPI.markAsRead(conversationId, { mark_all: true })

        // Update the unread count in the chat list without triggering reload
        setChatUpdates(prev => ({
          ...prev,
          [conversationId]: {
            unreadCount: 0
          }
        }))

        // Update read status for all messages in this conversation
        setMessages(prev => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).map(msg => ({
            ...msg,
            isRead: true
          }))
        }))
      } else {
      }
    } catch (err) {
      console.error('❌ Error marking conversation as read:', err)
    }
  }

  const addNewMessageToConversation = useCallback((conversationId, messageData) => {
    // Extract and normalize message ID early to check for duplicates
    // Use same extraction logic as all handlers for consistency
    const messageId = messageData.id || messageData.messageId || messageData.message_id;
    const normalizedMessageId = messageId ? String(messageId) : null;

    // Check if message was already processed - early return to prevent duplicates
    if (normalizedMessageId && processedMessages.current.has(normalizedMessageId)) {
      return;
    }

    // Mark message as processed IMMEDIATELY to prevent race conditions
    if (normalizedMessageId) {
      processedMessages.current.add(normalizedMessageId);
      // Clean up old processed messages to prevent memory leaks
      if (processedMessages.current.size > 100) {
        const firstMessage = processedMessages.current.values().next().value;
        processedMessages.current.delete(firstMessage);
      }
    }

    // Stop typing indicator for the sender when message is received
    const senderId = messageData.senderId || messageData.sender_id || messageData.sender?.id;
    if (senderId && conversationId) {
      stopTypingForUser(conversationId, senderId);
    }
    // Determine if message has media and set appropriate type
    const hasMedia = messageData.media && messageData.media.length > 0;

    // Helper function to detect if a media item is audio
    const isAudioMedia = (mediaItem) => {
      // Check explicit flags
      if (mediaItem.is_audio || mediaItem.isAudio || mediaItem.type === 'audio' || mediaItem.file_type === 'audio') {
        return true;
      }

      // Check mime type
      const mimeType = mediaItem.mime_type || mediaItem.type;
      if (mimeType && typeof mimeType === 'string' && mimeType.startsWith('audio/')) {
        return true;
      }

      // Check URL extension for common audio formats
      const url = mediaItem.url || '';
      if (typeof url === 'string') {
        const audioExtensions = ['.webm', '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.opus'];
        const lowerUrl = url.toLowerCase();
        if (audioExtensions.some(ext => lowerUrl.includes(ext))) {
          return true;
        }
      }

      // Check if message content indicates voice message
      const messageContent = messageData.message || messageData.content || '';
      if (typeof messageContent === 'string' &&
        (messageContent.toLowerCase().includes('voice') ||
          messageContent.toLowerCase().includes('audio'))) {
        return true;
      }

      return false;
    };

    // Check if any media item is audio
    const hasAudio = hasMedia && messageData.media.some(isAudioMedia);
    const messageType = hasAudio ? 'audio' : (hasMedia ? 'media' : (messageData.type || 'text'));

    // Format media data if present
    const formattedMedia = hasMedia ? messageData.media.map(mediaItem => {
      const isAudio = isAudioMedia(mediaItem);
      const mimeType = mediaItem.mime_type || mediaItem.type;

      return {
        url: mediaItem.url,
        name: mediaItem.file_name || mediaItem.name,
        type: mimeType || (isAudio ? 'audio/webm' : null),
        size: mediaItem.file_size || mediaItem.size,
        isImage: mediaItem.is_image || (mimeType && mimeType.startsWith('image/')),
        isVideo: mediaItem.is_video || (mimeType && mimeType.startsWith('video/') && !isAudio),
        isDocument: !isAudio && !mediaItem.is_image && !mediaItem.is_video &&
          !(mimeType && (mimeType.startsWith('image/') || mimeType.startsWith('video/') || mimeType.startsWith('audio/'))),
        isAudio: isAudio,
        caption: mediaItem.caption || null,
        duration: mediaItem.duration || null
      };
    }) : null;

    // Create a new message object in the expected format
    // Use normalized message ID to ensure consistency with processedMessages
    const newMessage = {
      id: normalizedMessageId || messageData.id || `msg-${Date.now()}-${Math.random()}`, // Use normalized ID first, then provided ID, or generate stable one
      messageId: normalizedMessageId || messageData.id || messageData.messageId, // Explicitly store the ID for deletion matching
      senderId: messageData.senderId,
      senderName: messageData.sender?.name || messageData.sender || 'Someone',
      content: messageData.message || messageData.content,
      timestamp: formatTimestamp(new Date()),
      originalTimestamp: new Date().toISOString(),
      type: messageType,
      user: {
        id: messageData.senderId,
        first_name: messageData.sender?.name || messageData.sender || 'Someone',
        last_name: '',
        profile_image_url: messageData.sender?.image || '/images/default.jpg'
      },
      isOwnMessage: false,
      isRead: true, // Mark as read immediately since user is viewing the conversation
      media: formattedMedia
    };

    // Check if this conversation is currently open
    const isCurrentConversation = selectedChat && conversationId && selectedChat.toString() === conversationId.toString();

    if (isCurrentConversation) {
      // Use direct state update with duplicate prevention to minimize re-renders
      setMessages(prev => {
        const currentMessages = prev[conversationId] || [];

        // Extract all possible message identifiers for comparison
        const newMessageId = newMessage.id;
        const newMessageIdAlt = messageData.id || messageData.messageId;
        const newMessageContent = newMessage.content;
        const newMessageTimestamp = newMessage.originalTimestamp;
        const newMessageSenderId = newMessage.senderId;

        // Only update if message doesn't already exist (prevent duplicates)
        // Check multiple identifiers to catch duplicates even if IDs differ slightly
        const messageExists = currentMessages.some(msg => {
          // Check by ID (primary check)
          if (newMessageId && (msg.id === newMessageId || msg.id === newMessageIdAlt)) {
            return true;
          }
          // Check by messageId field if it exists
          if (newMessageIdAlt && msg.messageId === newMessageIdAlt) {
            return true;
          }
          // Check by content + timestamp + sender (fallback for messages without IDs)
          if (newMessageContent && newMessageTimestamp && newMessageSenderId) {
            return msg.content === newMessageContent &&
              msg.originalTimestamp === newMessageTimestamp &&
              msg.senderId === newMessageSenderId;
          }
          return false;
        });

        if (messageExists) {
          return prev; // No change needed
        }
        return {
          ...prev,
          [conversationId]: [...currentMessages, newMessage]
        };
      });

      // Smart scroll to bottom for new messages
      setTimeout(() => {
        scrollToBottomSmooth();
      }, 100);
    } else {
      // If conversation is not open, add to newMessages for later merging
      setNewMessages(prev => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), newMessage]
      }));
    }

    // Update only the specific chat using chatUpdates to prevent re-rendering the entire list
    setChatUpdates(prev => ({
      ...prev,
      [conversationId]: {
        lastMessage: messageData.message,
        timestamp: formatTimestamp(new Date()),
        unreadCount: 0 // Set to 0 since user is actively viewing the conversation
      }
    }));

    // Automatically mark conversation as read since user is actively viewing it
    setTimeout(async () => {
      try {
        const currentChat = memoizedChats.find(chat => chat.id === conversationId);
        const currentMessages = messages[conversationId] || [];

        if (currentChat?.unreadCount > 0 || currentMessages.some(msg => !msg.isRead)) {
          await conversationsAPI.markAsRead(conversationId, { mark_all: true });

          // Update the unread count in the chat list without triggering reload
          setChatUpdates(prev => ({
            ...prev,
            [conversationId]: {
              unreadCount: 0
            }
          }));

          // Update read status for all messages in this conversation
          setMessages(prev => ({
            ...prev,
            [conversationId]: (prev[conversationId] || []).map(msg => ({
              ...msg,
              isRead: true
            }))
          }));
        }
      } catch (error) {
        console.error('❌ Error auto-marking conversation as read:', error);
      }
    }, 500); // Small delay to ensure message is added first
  }, [selectedChat, stopTypingForUser, memoizedChats, messages]);


  // Load chats from API
  const loadChats = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await conversationsAPI.getMedicalChats()
      const transformedChats = response.data.data.map(transformChatData).filter(chat => chat !== null)
      setChats(transformedChats)
      setChatUpdates({})
    } catch (err) {
      console.error('❌ Error loading chats:', err)
      let errorMessage = t("chat.errors.loadChats", { defaultValue: "Failed to load chats. Please try again." })

      if (err.response) {
        // Server responded with error status
        errorMessage = err.response.data?.message || t("chat.errors.server", { code: err.response.status, defaultValue: "Server error: {{code}}" })
      } else if (err.request) {
        // Network error
        errorMessage = t("chat.errors.network", { defaultValue: "Network error. Please check your internet connection." })
      } else {
        // Other error
        errorMessage = err.message || errorMessage
      }

      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const updateChatListWithNewMessage = (messageData) => {
    const senderId = messageData.senderId;
    const conversationId = messageData.conversationId || messageData.conversation_id;
    const conversationType = messageData.conversationType || messageData.conversation_type;

    // Strictly find by conversationId only to prevent leakage from social chats with same sender
    let existingChat = null;
    if (conversationId) {
      existingChat = memoizedChats.find((chat) => chat.id?.toString() === conversationId?.toString());
    }

    if (existingChat) {

      // Update the chat in the list and move it to the top (most recent first)
      setChats(prev => {
        // Find the chat in the current state (not memoized)
        const currentChat = prev.find(chat => chat.id === existingChat.id);
        if (!currentChat) {
          console.warn("Chat not found in current state, skipping update");
          return prev;
        }

        const updatedChat = {
          ...currentChat,
          lastMessage: messageData.message || messageData.content || messageData.lastMessage,
          timestamp: formatTimestamp(new Date()),
          unreadCount: selectedChat === existingChat.id ? 0 : (currentChat.unreadCount || 0) + 1,
        };

        // Mark this chat as recently updated for animation
        setRecentlyUpdatedChats(prev => {
          const newSet = new Set(prev);
          newSet.add(existingChat.id);
          return newSet;
        });

        // Remove the animation class after animation completes
        setTimeout(() => {
          setRecentlyUpdatedChats(prev => {
            const newSet = new Set(prev);
            newSet.delete(existingChat.id);
            return newSet;
          });
        }, 500); // Match animation duration

        // Remove the chat from its current position and add it to the top
        const otherChats = prev.filter(chat => chat.id !== existingChat.id);
        return [updatedChat, ...otherChats];
      });

      // Clear any pending updates for this chat since we updated it directly
      setChatUpdates((prev) => {
        const { [existingChat.id]: removed, ...rest } = prev;
        return rest;
      });
    } else {

      // For medical conversations, if not found, try to reload chats or add it
      if (conversationType === 'medical' && conversationId) {
        // Try to get the conversation from API
        handleChatWithReceiver(conversationId, 'medical').catch(err => {
          console.error("❌ Error loading medical conversation:", err);
        });
      }
    }
  };

  // Subscribe to WebSocket channels for all conversations
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    // Subscribe to all conversation channels from current chats
    const channelSubscriptions = [];

    memoizedChats.forEach((chat) => {
      const channelName = `conversation.${chat.id}`;

      try {
        // Subscribe to the channel
        const channel = subscribe(channelName, null);

        if (channel) {
          // Listen for message.sent events
          listen(channelName, 'message.sent', (data) => {

            // Helper function to detect if a media item is audio
            const isAudioMediaItem = (item) => {
              // Check explicit flags
              if (item.type === 'audio' || item.is_audio || item.isAudio || item.file_type === 'audio') {
                return true;
              }

              // Check mime type
              const mimeType = item.mime_type || item.type;
              if (mimeType && typeof mimeType === 'string' && mimeType.startsWith('audio/')) {
                return true;
              }

              // Check URL extension for common audio formats
              const url = item.url || '';
              if (typeof url === 'string') {
                const audioExtensions = ['.webm', '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.opus'];
                const lowerUrl = url.toLowerCase();
                if (audioExtensions.some(ext => lowerUrl.includes(ext))) {
                  return true;
                }
              }

              // Check if message content indicates voice message
              const messageContent = data.content || '';
              if (typeof messageContent === 'string' &&
                (messageContent.toLowerCase().includes('voice') ||
                  messageContent.toLowerCase().includes('audio'))) {
                return true;
              }

              return false;
            };

            // Check if message is audio type
            const isAudioMessage = data.media && data.media.some(isAudioMediaItem);

            // Transform WebSocket event data to match existing message format
            // DEEP FILTERING: Check if this message is truly a medical conversation
            // This is critical when the same person has both social and medical chats
            const conversationType = data.conversation_type || data.conversationType ||
              (data.medical_chat_id ? 'medical' : 'social');

            if (conversationType !== 'medical') {
              return;
            }

            const messageData = {
              id: data.id,
              messageId: data.id,
              content: data.content,
              message: data.content,
              senderId: data.user_id,
              senderName: `${data.user.first_name} ${data.user.last_name}`.trim(),
              user: data.user,
              timestamp: data.created_at,
              originalTimestamp: data.created_at,
              type: isAudioMessage ? 'audio' : (data.media && data.media.length > 0 ? 'media' : 'text'),
              isOwnMessage: false,
              isRead: false,
              conversationType: 'medical', // Explicitly mark as medical
              media: data.media ? data.media.map((mediaItem) => {
                const isAudio = isAudioMediaItem(mediaItem);
                const mediaType = mediaItem.mime_type || mediaItem.type;

                return {
                  url: mediaItem.url,
                  name: mediaItem.name,
                  type: mediaType || (isAudio ? 'audio/webm' : null),
                  size: mediaItem.size,
                  isImage: mediaItem.type === 'image' || (mediaType && mediaType.startsWith('image/')),
                  isVideo: mediaItem.type === 'video' || (mediaType && mediaType.startsWith('video/') && !isAudio),
                  isDocument: !isAudio &&
                    !['image', 'video', 'audio'].includes(mediaItem.type) &&
                    !(mediaType && (mediaType.startsWith('image/') || mediaType.startsWith('video/') || mediaType.startsWith('audio/'))),
                  isAudio: isAudio,
                };
              }) : null,
            };

            // Prevent duplicate processing
            // Normalize message ID to string for consistent comparison
            const messageId = messageData.id;
            const normalizedMessageId = messageId ? String(messageId) : null;

            if (normalizedMessageId && processedMessages.current.has(normalizedMessageId)) {
              return;
            }

            // Mark message as processed IMMEDIATELY
            if (normalizedMessageId) {
              processedMessages.current.add(normalizedMessageId);
              if (processedMessages.current.size > 100) {
                const firstMessage = processedMessages.current.values().next().value;
                processedMessages.current.delete(firstMessage);
              }
            }

            // Stop typing indicator for sender
            if (messageData.senderId && chat.id) {
              stopTypingForUser(chat.id, messageData.senderId);
            }

            // If this is the currently selected chat, add message to conversation
            if (selectedChat && selectedChat.toString() === chat.id.toString()) {
              addNewMessageToConversation(selectedChat, messageData);
            } else {
              // Otherwise, update chat list
              updateChatListWithNewMessage(messageData);
            }
          });

          // Listen for message.deleted events
          listen(channelName, 'message.deleted', (data) => {
            const messageId = data.message_id || data.id;
            if (!messageId) return;

            // 1. Set isDeleting: true for animation
            setMessages(prev => {
              const currentMessages = prev[chat.id] || [];
              return {
                ...prev,
                [chat.id]: currentMessages.map(msg =>
                  (String(msg.id) === String(messageId) ||
                    String(msg.messageId) === String(messageId) ||
                    String(msg.message_id) === String(messageId))
                    ? { ...msg, isDeleting: true }
                    : msg
                )
              };
            });

            // 2. Remove message after animation (400ms)
            setTimeout(() => {
              setMessages(prev => {
                const currentMessages = prev[chat.id] || [];
                const updatedMessages = currentMessages.filter(msg =>
                  String(msg.id) !== String(messageId) &&
                  String(msg.messageId) !== String(messageId) &&
                  String(msg.message_id) !== String(messageId)
                );
                return { ...prev, [chat.id]: updatedMessages };
              });
            }, 400);

            // 3. Update chat list if this was the last message
            setChats(prev => prev.map(c => {
              if (String(c.id) === String(chat.id)) {
                return { ...c, lastMessage: "Message deleted" };
              }
              return c;
            }));
          });


          channelSubscriptions.push(channelName);
        }
      } catch (error) {
        console.error(`Error subscribing to channel ${channelName}:`, error);
      }
    });

    // Cleanup: unsubscribe from all channels
    return () => {
      channelSubscriptions.forEach((channelName) => {
        unsubscribe(channelName);
      });
    };
  }, [isConnected, memoizedChats, selectedChat, subscribe, unsubscribe, listen]);

  // Handle medical conversation notifications (similar to ChatPage)
  const handleMedicalConversationNotification = useCallback(async (notification) => {
    // Check if this is a medical conversation
    const conversationType = notification.conversation_type || notification.conversationType;
    if (conversationType && conversationType !== 'medical') {
      return;
    }

    try {

      // Skip typing notifications - they should not be added as messages
      if (notification.type === 'user_typing' || notification.type === 'user_stop_typing') {
        return;
      }

      // Handle unified message_received format with conversation_type: 'medical'
      // Supports both unified format (type: 'message_received', conversation_type: 'medical') 
      // and legacy format for backward compatibility

      // Extract conversation ID from notification
      const conversationId = notification.conversation_id ||
        notification.target_id ||
        (notification.action_url ? parseInt(notification.action_url.split('/chat/')[1]?.split('/')[0] ||
          notification.action_url.split('/medical/clinic/')[1]?.split('/')[0]) : null);

      if (!conversationId) {
        console.warn('🏥 No conversation ID found in medical notification');
        return;
      }

      // Extract message ID early to check for duplicates
      // Normalize message ID to string for consistent comparison
      const messageId = notification.id || notification.messageId || notification.message_id;
      const normalizedMessageId = messageId ? String(messageId) : null;

      // Prevent duplicate processing by checking if this message was already processed
      if (normalizedMessageId && processedMessages.current.has(normalizedMessageId)) {
        return;
      }

      // Check if this is a sender notification (message sent by current user)
      // Unified format uses is_sender_notification flag
      const isSenderNotification = notification.is_sender_notification === true;

      // Get current user information
      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      const currentUserId = userData.user_id || userData.id;

      // Build sender object
      const sender = {
        id: notification.sender_id,
        name: notification.sender_name || 'Medical Professional',
        image: notification.sender_image || null
      };

      // Transform media array if present
      const formattedMedia = notification.media && notification.media.length > 0
        ? notification.media.map(mediaItem => ({
          url: mediaItem.url,
          name: mediaItem.name || mediaItem.file_name,
          type: mediaItem.type || mediaItem.mime_type,
          mime_type: mediaItem.mime_type || mediaItem.type,
          size: mediaItem.size || mediaItem.file_size,
          file_size: mediaItem.file_size || mediaItem.size,
          file_name: mediaItem.file_name || mediaItem.name,
          is_image: mediaItem.is_image || mediaItem.isImage,
          is_video: mediaItem.is_video || mediaItem.isVideo,
          is_document: mediaItem.is_document || mediaItem.isDocument,
          is_audio: mediaItem.is_audio || mediaItem.isAudio,
          isImage: mediaItem.is_image || mediaItem.isImage,
          isVideo: mediaItem.is_video || mediaItem.isVideo,
          isDocument: mediaItem.is_document || mediaItem.isDocument,
          isAudio: mediaItem.is_audio || mediaItem.isAudio
        }))
        : [];

      // Determine if message is from current user
      const isOwnMessage = isSenderNotification ||
        (currentUserId && sender.id && String(currentUserId) === String(sender.id));

      // Mark message as processed IMMEDIATELY before creating messageData to prevent race conditions
      if (normalizedMessageId) {
        processedMessages.current.add(normalizedMessageId);
        // Clean up old processed messages to prevent memory leaks
        if (processedMessages.current.size > 100) {
          const firstMessage = processedMessages.current.values().next().value;
          processedMessages.current.delete(firstMessage);
        }
      }

      // Create message data in expected format
      const messageData = {
        id: notification.id || `medical-${Date.now()}`,
        messageId: notification.id,
        content: notification.content || '',
        message: notification.content || '',
        media: formattedMedia,
        senderId: sender.id,
        sender: sender,
        timestamp: notification.created_at || new Date().toISOString(),
        isOwn: isOwnMessage,
        isOwnMessage: isOwnMessage,
        hasMedia: formattedMedia.length > 0,
        mediaCount: formattedMedia.length,
        conversationType: 'medical',
        conversationId: conversationId
      };

      // Check if conversation exists and update it (always update, even if not selected)

      // Check if conversation is selected - use both selectedChat state and URL path for reliability
      const currentPath = window.location.pathname;
      const isSelectedInPath = conversationId && (
        currentPath === `/medical/clinic/chat/${conversationId}` ||
        currentPath.includes(`/medical/clinic/chat/${conversationId}/`) ||
        currentPath.includes(`medical/clinic/chat/${conversationId}`)
      );
      const isSelectedInState = selectedChat && selectedChat.toString() === conversationId.toString();
      const isCurrentlySelected = isSelectedInPath || isSelectedInState;


      // Always update the chat list - check if it exists first
      let chatWasFound = false;
      setChats(prev => {
        const currentChat = prev.find(chat => chat.id === conversationId);

        if (currentChat) {
          chatWasFound = true;

          // Get current unread count
          const currentUnreadCount = currentChat.unreadCount || 0;

          // Only increment unread if conversation is NOT currently selected
          const newUnreadCount = isCurrentlySelected ? 0 : (currentUnreadCount + 1);

          const updatedChat = {
            ...currentChat,
            lastMessage: messageData.content || messageData.message || currentChat.lastMessage,
            timestamp: formatTimestamp(new Date()),
            unreadCount: newUnreadCount,
          };

          // Mark this chat as recently updated for animation
          setRecentlyUpdatedChats(prev => {
            const newSet = new Set(prev);
            newSet.add(conversationId);
            return newSet;
          });

          // Remove the animation class after animation completes
          setTimeout(() => {
            setRecentlyUpdatedChats(prev => {
              const newSet = new Set(prev);
              newSet.delete(conversationId);
              return newSet;
            });
          }, 500); // Match animation duration

          // Move to top
          const otherChats = prev.filter(chat => chat.id !== conversationId);
          const newChatsList = [updatedChat, ...otherChats];
          return newChatsList;
        } else {
          // Don't modify state if not found - will be handled below
          return prev;
        }
      });

      // Clear any pending updates since we updated directly
      setChatUpdates(prev => {
        const { [conversationId]: removed, ...rest } = prev;
        return rest;
      });

      // If chat was found and updated, we're done with the list update
      if (chatWasFound) {
        // If this is the currently selected chat, also add message to conversation
        if (isCurrentlySelected) {
          addNewMessageToConversation(conversationId, messageData);
        } else {
        }
        return; // Exit early since we've updated the list
      }

      // If chat was not found, just update chat list with new message data
      // Don't call loadChats() API - update state directly from notification

      // Update chat list directly with message data from notification
      updateChatListWithNewMessage(messageData);

      // If conversation is selected, add message to conversation
      if (isCurrentlySelected) {
        addNewMessageToConversation(conversationId, messageData);
      }

    } catch (error) {
      console.error('❌ Error handling medical conversation notification:', error);
    }
  }, [selectedChat, addNewMessageToConversation, updateChatListWithNewMessage, formatTimestamp]);

  // Listen for new message events when on chat page
  useEffect(() => {
    const handleNewMessageReceived = (event) => {
      const messageData = event.detail;

      // Handle unified message_received notifications
      // Check if this is a medical conversation using conversation_type field
      const conversationType = messageData.conversation_type || messageData.conversationType;
      if (conversationType !== 'medical') {
        return;
      }

      // Ignore deleted messages early
      if (messageData.is_deleted === true || messageData.type === 'message_deleted') {
        return;
      }

      // Prevent duplicate processing by checking if this message was already processed
      // Normalize message ID to string for consistent comparison
      const messageId = messageData.id || messageData.messageId;
      const normalizedMessageId = messageId ? String(messageId) : null;

      if (normalizedMessageId && processedMessages.current.has(normalizedMessageId)) {
        return;
      }

      // Mark message as processed IMMEDIATELY to prevent race conditions
      if (normalizedMessageId) {
        processedMessages.current.add(normalizedMessageId);
        // Clean up old processed messages to prevent memory leaks
        if (processedMessages.current.size > 100) {
          const firstMessage = processedMessages.current.values().next().value;
          processedMessages.current.delete(firstMessage);
        }
      }

      // Extract conversation and sender IDs from unified format
      const incomingConversationId = messageData.conversationId || messageData.conversation_id;
      const senderId = messageData.senderId || messageData.sender_id || messageData.sender?.id;

      // Transform notification data to match expected format
      const transformedMessageData = {
        ...messageData,
        conversationId: incomingConversationId,
        conversation_id: incomingConversationId,
        content: messageData.message || messageData.content,
        // Ensure media array is properly formatted
        media: messageData.media ? messageData.media.map(mediaItem => ({
          ...mediaItem,
          url: mediaItem.url,
          name: mediaItem.name || mediaItem.file_name,
          type: mediaItem.type || mediaItem.mime_type,
          mime_type: mediaItem.mime_type || mediaItem.type,
          size: mediaItem.size || mediaItem.file_size,
          file_size: mediaItem.file_size || mediaItem.size,
          file_name: mediaItem.file_name || mediaItem.name,
          is_image: mediaItem.is_image || mediaItem.isImage,
          is_video: mediaItem.is_video || mediaItem.isVideo,
          is_document: mediaItem.is_document || mediaItem.isDocument,
          is_audio: mediaItem.is_audio || mediaItem.isAudio,
          // Keep original properties for compatibility
          isImage: mediaItem.is_image || mediaItem.isImage,
          isVideo: mediaItem.is_video || mediaItem.isVideo,
          isDocument: mediaItem.is_document || mediaItem.isDocument,
          isAudio: mediaItem.is_audio || mediaItem.isAudio
        })) : null
      };

      // Determine if the message belongs to the currently selected chat
      let isForSelectedChat = false;
      if (selectedChat) {
        if (incomingConversationId && selectedChat.toString() === incomingConversationId.toString()) {
          isForSelectedChat = true;
        }
      }

      if (isForSelectedChat) {
        // Stop typing indicator for sender when message is received
        if (senderId) {
          stopTypingForUser(selectedChat, senderId);
        }
        // Add the new message directly to the conversation instead of reloading
        addNewMessageToConversation(selectedChat, transformedMessageData);
        // Also update chat list to move conversation to top
        updateChatListWithNewMessage(transformedMessageData);
      } else {
        // Not for open chat, find if it matches another existing chat
        let targetConversation = null;
        if (incomingConversationId) {
          targetConversation = memoizedChats.find(chat => chat.id?.toString() === incomingConversationId.toString());
        }

        if (targetConversation) {
          addNewMessageToConversation(targetConversation.id, transformedMessageData);
        } else {
          // Check if this is a known conversation id before updating list
          // This prevents social messages that don't match any medical chat from showing up
          updateChatListWithNewMessage(transformedMessageData);
        }
      }
    };

    // Handle message sent sync events (for syncing conversations across devices)
    const handleMessageSentSync = (event) => {
      const syncData = event.detail;

      // Only process if this is a sender notification (message sent by current user)
      // Check unified format: type === 'message_received' and is_sender_notification === true
      const isSenderNotification = syncData.isSenderNotification === true ||
        syncData.is_sender_notification === true ||
        (syncData.type === 'message_received' && syncData.is_sender_notification === true);

      if (!isSenderNotification) {
        return;
      }

      // Check if this is a medical conversation
      const conversationType = syncData.conversation_type || syncData.conversationType;
      if (conversationType !== 'medical') {
        return;
      }

      // Prevent duplicate processing by checking if this message was already processed
      // Normalize message ID to string for consistent comparison
      const syncMessageId = syncData.messageId || syncData.id;
      const normalizedSyncMessageId = syncMessageId ? String(syncMessageId) : null;

      if (normalizedSyncMessageId && processedMessages.current.has(normalizedSyncMessageId)) {
        return;
      }

      // Mark message as processed IMMEDIATELY to prevent race conditions
      if (normalizedSyncMessageId) {
        processedMessages.current.add(normalizedSyncMessageId);
        // Clean up old processed messages to prevent memory leaks
        if (processedMessages.current.size > 100) {
          const firstMessage = processedMessages.current.values().next().value;
          processedMessages.current.delete(firstMessage);
        }
      }

      // Extract data from unified format
      const conversationId = syncData.conversationId || syncData.conversation_id;
      const messageId = syncData.messageId || syncData.id;
      const message = syncData.message || syncData.content || '';
      const media = syncData.media || [];
      const recipient = syncData.recipient || syncData.sender || {};

      // Find the chat that matches this conversation
      const targetChat = memoizedChats.find(chat => chat.id === conversationId);

      if (targetChat) {

        // Create message data in the expected format
        const messageData = {
          id: messageId,
          content: message,
          sender: {
            id: recipient.id, // The recipient in the sync data is actually the sender
            name: targetChat.otherParticipant?.name || 'You',
            image: targetChat.otherParticipant?.image || null
          },
          timestamp: new Date().toISOString(),
          isOwn: true, // This is a message sent by the current user
          // Add media-specific fields for proper display
          hasMedia: media && media.length > 0,
          mediaCount: media ? media.length : 0,
          // Ensure media is properly formatted for display (matching ChatPage component expectations)
          media: media ? media.map(mediaItem => ({
            url: mediaItem.url,
            name: mediaItem.file_name,
            type: mediaItem.mime_type,
            size: mediaItem.file_size,
            isImage: mediaItem.is_image,
            isVideo: mediaItem.is_video,
            isDocument: mediaItem.is_document,
            caption: mediaItem.caption || null
          })) : []
        };

        // Add the message to the conversation
        addNewMessageToConversation(conversationId, messageData);

        // Update the chat list to show the new message
        updateChatListWithNewMessage(messageData);
      } else {
        // Don't call loadChats() API - update state directly from notification
        updateChatListWithNewMessage(messageData);
      }
    };

    // Handle chat message refresh events (when actively viewing a chat)
    const handleRefreshChatMessages = (event) => {
      const refreshData = event.detail;

      // Check if this is a medical conversation
      const conversationType = refreshData.conversationType || refreshData.conversation_type;
      if (conversationType !== 'medical') {
        return;
      }

      const { conversationId, messageId, message, media } = refreshData;

      // Only refresh if this is the currently selected chat
      if (selectedChat && selectedChat.toString() === conversationId.toString()) {

        // Create message data
        const messageData = {
          id: messageId,
          content: message,
          media: media ? media.map(mediaItem => ({
            url: mediaItem.url,
            name: mediaItem.file_name,
            type: mediaItem.mime_type,
            size: mediaItem.file_size,
            isImage: mediaItem.is_image,
            isVideo: mediaItem.is_video,
            isDocument: mediaItem.is_document,
            caption: mediaItem.caption || null
          })) : [],
          sender: {
            id: memoizedChats.find(chat => chat.id === conversationId)?.otherParticipant?.id,
            name: memoizedChats.find(chat => chat.id === conversationId)?.otherParticipant?.name || 'You',
            image: memoizedChats.find(chat => chat.id === conversationId)?.otherParticipant?.image || null
          },
          timestamp: new Date().toISOString(),
          isOwn: true
        };

        // Add the message to the current conversation
        addNewMessageToConversation(conversationId, messageData);
      }
    };

    // Handle medical chat badge update events
    const handleMedicalChatBadgeUpdate = (event) => {
      const medicalData = event.detail;

      // Don't call loadChats() API - update state directly from notification
      // Update medical chat list with new message data
      const messageData = {
        id: medicalData.messageId || `medical-${Date.now()}`,
        message: medicalData.message,
        sender: medicalData.sender,
        senderId: medicalData.sender?.id,
        media: medicalData.media || [],
        hasMedia: medicalData.hasMedia || false,
        conversationType: 'medical',
        timestamp: medicalData.timestamp || new Date().toISOString()
      };

      // Update chat list with medical message
      updateChatListWithNewMessage(messageData);

      // If we have a selected chat, check if it matches the medical conversation
      if (selectedChat) {
        // Primary match: by conversation ID
        if (medicalData.conversationId && selectedChat.toString() === medicalData.conversationId.toString()) {
          addNewMessageToConversation(selectedChat, messageData);
        }
        // Fallback match: by participant ID
        else {
          const currentChatObj = memoizedChats.find(chat => chat.id && chat.id.toString() === selectedChat.toString());
          const currentChatParticipantId = currentChatObj?.otherParticipant?.id || currentChatObj?.senderId;
          if (currentChatParticipantId && currentChatParticipantId.toString() === messageData.senderId?.toString()) {
            addNewMessageToConversation(selectedChat, messageData);
          }
        }
      }
    };

    // Handle conversation closed events
    const handleConversationClosed = (event) => {
      const closedData = event.detail;

      const { conversationId, message, closedBy, timestamp } = closedData;

      // Update the specific chat to show it's closed
      setChatUpdates(prev => ({
        ...prev,
        [conversationId]: {
          isClosed: true,
          closedAt: timestamp,
          closedBy: closedBy,
          lastMessage: message || 'Conversation ended'
        }
      }));

      // If this is the currently selected chat, update the UI immediately
      if (selectedChat && selectedChat.toString() === conversationId.toString()) {

        // Add a system message to show the conversation was closed
        const systemMessage = {
          id: `system-closed-${Date.now()}`,
          senderId: "system",
          senderName: "System",
          content: message || 'This conversation has been ended',
          timestamp: formatTimestamp(new Date()),
          originalTimestamp: new Date().toISOString(),
          type: "system",
          user: {
            id: "system",
            first_name: "System",
            last_name: "",
            profile_image_url: "/images/default.jpg"
          },
          isOwnMessage: false,
          isRead: true,
          isSystemMessage: true
        };

        // Add the system message to the conversation
        setMessages(prev => {
          const currentMessages = prev[conversationId] || [];
          return {
            ...prev,
            [conversationId]: [...currentMessages, systemMessage]
          };
        });

        // Scroll to show the new system message
        setTimeout(() => {
          scrollToBottomSmooth();
        }, 100);
      }
    };

    // Handle message deletion sync from notifications
    const handleMessageDeletedSync = (event) => {
      const { messageId, message_id, id, conversationId, conversationType } = event.detail;
      const actualMessageId = messageId || message_id || id;

      // Bypass processedMessages check for deletions


      // Only handle medical here (ChatPage handles social)
      if (conversationType !== 'medical') {
        return;
      }

      if (messageId && conversationId) {
        const chatId = String(conversationId);

        // 1. Set isDeleting: true for animation
        setMessages(prev => {
          const currentMessages = prev[chatId] || [];
          return {
            ...prev,
            [chatId]: currentMessages.map(msg =>
              (String(msg.id) === String(actualMessageId) ||
                String(msg.messageId) === String(actualMessageId) ||
                String(msg.message_id) === String(actualMessageId))
                ? { ...msg, isDeleting: true }
                : msg
            )
          };
        });

        // 2. Remove message after animation (400ms)
        setTimeout(() => {
          setMessages(prev => {
            const currentMessages = prev[chatId] || [];
            const updatedMessages = currentMessages.filter(msg =>
              String(msg.id) !== String(actualMessageId) &&
              String(msg.messageId) !== String(actualMessageId) &&
              String(msg.message_id) !== String(actualMessageId)
            );
            return { ...prev, [chatId]: updatedMessages };
          });
        }, 400);

        // 3. Also update last message in chat list if needed
        setChats(prev => prev.map(c => {
          if (String(c.id) === chatId) {
            return {
              ...c,
              lastMessage: { ...c.lastMessage, is_deleted: true, content: "Message deleted" }
            };
          }
          return c;
        }));

      }
    };


    // Handle direct chat list updates (e.g. from external events)
    const handleUpdateChatListDirect = (event) => {
      const messageData = event.detail;
      if (!messageData) return;

      // Only handle medical conversations
      const conversationType = messageData.conversation_type || messageData.conversationType;
      if (conversationType && conversationType !== 'medical') return;

      updateChatListWithNewMessage(messageData);
    };

    // Add event listeners
    window.addEventListener('new-message-received', handleNewMessageReceived);
    window.addEventListener('message-sent-sync', handleMessageSentSync);
    window.addEventListener('refresh-chat-messages', handleRefreshChatMessages);
    window.addEventListener('message-deleted-sync', handleMessageDeletedSync);
    window.addEventListener('medical-chat-badge-update', handleMedicalChatBadgeUpdate);
    window.addEventListener('conversationClosed', handleConversationClosed);
    window.addEventListener('update-chat-list-direct', handleUpdateChatListDirect);


    // Listen for medical conversation notifications
    // This can be triggered by the notification system or directly
    const handleMedicalNotificationEvent = (event) => {
      handleMedicalConversationNotification(event.detail || event);
    };

    window.addEventListener('medical-conversation-notification', handleMedicalNotificationEvent);

    // Also listen for the notification format from useNotifications hook
    const handleNotificationEvent = (event) => {
      const notification = event.detail || event;

      // Skip typing notifications - they are handled separately
      if (notification.type === 'user_typing' || notification.type === 'user_stop_typing') {
        return;
      }

      // Check for duplicate BEFORE processing - extract message ID early
      const messageId = notification.id || notification.messageId || notification.message_id;
      const normalizedMessageId = messageId ? String(messageId) : null;

      // Only check for duplicates for new messages, NOT for deletions
      if (notification.type !== 'message_deleted' &&
        normalizedMessageId &&
        processedMessages.current.has(normalizedMessageId)) {
        return;
      }

      if ((notification.type === 'message_received' && notification.conversation_type === 'medical') ||
        notification.conversation_type === 'medical') {
        handleMedicalConversationNotification(notification);
      }

      // Handle message_deleted for medical if received via notification-received
      if (notification.type === 'message_deleted' && notification.conversation_type === 'medical') {
        const messageId = notification.message_id || notification.id;
        const conversationId = notification.conversation_id || notification.target_id;

        console.log('🗑️ [doctorchat] Deletion notification received:', { messageId, conversationId });

        if (messageId && conversationId) {
          const chatId = String(conversationId);

          // 1. Set isDeleting: true for animation
          setMessages(prev => {
            const currentMessages = prev[chatId] || [];
            console.log(`🗑️ [doctorchat] Current messages count for ${chatId}:`, currentMessages.length);

            return {
              ...prev,
              [chatId]: currentMessages.map(msg => {
                const matches = (String(msg.id) === String(messageId) ||
                  String(msg.messageId) === String(messageId) ||
                  String(msg.message_id) === String(messageId));

                if (matches) console.log('🗑️ [doctorchat] Found message to animate:', msg.id);
                return matches ? { ...msg, isDeleting: true } : msg;
              })
            };
          });

          // 2. Remove message after animation (400ms)
          setTimeout(() => {
            setMessages(prev => {
              const currentMessages = prev[chatId] || [];
              const updatedMessages = currentMessages.filter(msg =>
                String(msg.id) !== String(messageId) &&
                String(msg.messageId) !== String(messageId) &&
                String(msg.message_id) !== String(messageId)
              );
              return { ...prev, [chatId]: updatedMessages };
            });
          }, 400);

          // 3. Update chat list
          setChats(prev => prev.map(c => {
            if (String(c.id) === chatId) {
              return { ...c, lastMessage: "Message deleted" };
            }
            return c;
          }));
        }
      }

    };
    window.addEventListener('notification-received', handleNotificationEvent);


    // Also listen for raw notification objects that might come directly from Echo/backend
    // This handles notifications that haven't been processed by useNotifications yet
    const handleRawNotification = (event) => {
      const notification = event.detail || event;

      // Skip typing notifications - they should not be added as messages
      if (notification.type === 'user_typing' || notification.type === 'user_stop_typing') {
        return;
      }

      // Check for duplicate BEFORE processing - extract message ID early
      const messageId = notification.id || notification.messageId || notification.message_id;
      const normalizedMessageId = messageId ? String(messageId) : null;

      // Only check for duplicates for new messages, NOT for deletions
      if (notification.type !== 'message_deleted' &&
        normalizedMessageId &&
        processedMessages.current.has(normalizedMessageId)) {
        return;
      }

      // Check if it's a medical conversation notification
      if ((notification.type === 'message_received' && notification.conversation_type === 'medical') ||
        (notification.conversation_type === 'medical' && notification.conversation_id)) {
        handleMedicalConversationNotification(notification);
      }
    };

    // Handle messages-read event (when another user marks messages as read)
    const handleMessagesRead = (event) => {
      const eventData = event.detail;

      const conversationId = eventData.conversationId || eventData.conversation_id;

      if (!conversationId) {
        console.warn('⚠️ Messages read event missing conversation_id');
        return;
      }

      // Check if this is a medical conversation
      const conversationType = eventData.conversationType || eventData.conversation_type;
      if (conversationType !== 'medical') {
        return;
      }

      // Update messages state to mark all messages as read
      setMessages(prev => {
        const conversationMessages = prev[conversationId];
        if (!conversationMessages || conversationMessages.length === 0) {
          return prev;
        }

        // Mark all messages in the conversation as read
        return {
          ...prev,
          [conversationId]: conversationMessages.map(msg => ({
            ...msg,
            isRead: true
          }))
        };
      });

      // Update chat list unread count to 0
      setChatUpdates(prev => ({
        ...prev,
        [conversationId]: {
          ...prev[conversationId],
          unreadCount: 0
        }
      }));

    };

    // Listen for notifications that might come directly from the notification system
    window.addEventListener('notification', handleRawNotification);

    // Listen for messages-read event (when another user marks messages as read)
    window.addEventListener('messages-read', handleMessagesRead);

    // Handle typing notifications from notification system
    const handleTypingNotification = (event) => {
      const notification = event.detail || event;
      // Check if it's a typing notification
      if (notification.type !== 'user_typing') {
        return;
      }

      // Extract notification data - handle both nested and flat structures
      let notificationData;
      if (typeof notification.data === 'string') {
        try {
          notificationData = JSON.parse(notification.data);
        } catch (e) {
          notificationData = notification;
        }
      } else if (notification.data && typeof notification.data === 'object') {
        notificationData = notification.data;
      } else {
        notificationData = notification;
      }

      const conversationType = notificationData.conversation_type || notificationData.conversationType ||
        notification.conversation_type || notification.conversationType;

      if (conversationType && conversationType !== 'medical') {
        return;
      }

      if (!conversationId) {
        console.warn('⌨️ Typing notification missing conversation_id');
        return;
      }

      if (!senderId) {
        console.warn('⌨️ Typing notification missing sender_id');
        return;
      }

      const convIdStr = conversationId.toString();
      const timeoutKey = `${convIdStr}_${senderId}`;

      // Clear existing timeout for this user in this conversation
      if (typingTimeoutsRef.current[timeoutKey]) {
        clearTimeout(typingTimeoutsRef.current[timeoutKey]);
        delete typingTimeoutsRef.current[timeoutKey];
      }

      if (isTyping) {
        // Add user to typing set for this conversation
        setConversationTypingUsers(prev => {
          const updated = { ...prev };
          if (!updated[convIdStr]) {
            updated[convIdStr] = new Set();
          }
          updated[convIdStr] = new Set(updated[convIdStr]);
          updated[convIdStr].add(senderId);
          return updated;
        });

        // Auto-remove after 5 seconds if no stopped typing notification
        typingTimeoutsRef.current[timeoutKey] = setTimeout(() => {
          stopTypingForUser(conversationId, senderId);
        }, 5000);
      } else {
        // Remove user from typing set for this conversation
        stopTypingForUser(conversationId, senderId);
      }
    };

    // Handle user_stop_typing event from notification system
    const handleStopTypingNotification = (event) => {
      const notification = event.detail || event;

      // Extract notification data - handle both nested and flat structures
      let notificationData;
      if (typeof notification.data === 'string') {
        try {
          notificationData = JSON.parse(notification.data);
        } catch (e) {
          notificationData = notification;
        }
      } else if (notification.data && typeof notification.data === 'object') {
        notificationData = notification.data;
      } else {
        notificationData = notification;
      }

      // Try multiple paths to get the values
      const conversationId = notificationData.conversation_id ||
        notification.conversation_id ||
        notificationData.target_id ||
        notification.target_id;
      const senderId = notificationData.sender_id ||
        notification.sender_id ||
        notificationData.user_id ||
        notification.user_id;

      if (!conversationId) {
        console.warn('⌨️ Stop typing notification missing conversation_id');
        return;
      }

      if (!senderId) {
        console.warn('⌨️ Stop typing notification missing sender_id');
        return;
      }


      // Remove user from typing set for this conversation
      stopTypingForUser(conversationId, senderId);
    };

    // Listen for typing notifications
    window.addEventListener('notification-received', handleTypingNotification);
    window.addEventListener('notification', handleTypingNotification);

    // Listen for stop typing notifications
    window.addEventListener('user_stop_typing', handleStopTypingNotification);

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('new-message-received', handleNewMessageReceived);
      window.removeEventListener('message-sent-sync', handleMessageSentSync);
      window.removeEventListener('refresh-chat-messages', handleRefreshChatMessages);
      window.removeEventListener('message-deleted-sync', handleMessageDeletedSync);
      window.removeEventListener('medical-chat-badge-update', handleMedicalChatBadgeUpdate);
      window.removeEventListener('conversationClosed', handleConversationClosed);
      window.removeEventListener('update-chat-list-direct', handleUpdateChatListDirect);

      window.removeEventListener('medical-conversation-notification', handleMedicalNotificationEvent);
      window.removeEventListener('notification-received', handleNotificationEvent);
      window.removeEventListener('notification', handleRawNotification);
      window.removeEventListener('messages-read', handleMessagesRead);
      window.removeEventListener('notification-received', handleTypingNotification);
      window.removeEventListener('notification', handleTypingNotification);
      window.removeEventListener('user_stop_typing', handleStopTypingNotification);

      // Clear all typing timeouts
      Object.values(typingTimeoutsRef.current).forEach(timeout => clearTimeout(timeout));
      typingTimeoutsRef.current = {};
    };
  }, [selectedChat, stopTypingForUser, memoizedChats, addNewMessageToConversation, handleMedicalConversationNotification]); // Added dependencies to prevent stale closures

  // Load chats on component mount
  const chatsLoadedRef = useRef(false);
  useEffect(() => {
    if (!chatsLoadedRef.current) {
      chatsLoadedRef.current = true;
      loadChats();
    }
  }, [])

  // Medical chat is always active
  useEffect(() => {
  }, []);

  // Handle window resize - close mobile sidebar when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 767 && isMobileSidebarOpen) {
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileSidebarOpen]);

  // Add test function for development
  useEffect(() => {
    if (import.meta.env.DEV) {
      // Expose test function globally for development
      window.testMessageSentSync = () => {
        const testData = {
          conversationId: 38,
          messageId: 573,
          message: "Test sync message",
          media: [],
          recipient: { id: 139 }
        };

        const syncEvent = new CustomEvent('message-sent-sync', {
          detail: testData
        });
        window.dispatchEvent(syncEvent);
      };

      // Test function for media display
      window.testMediaDisplay = () => {
        const testMessageData = {
          id: 999,
          message: "Test message with media",
          media: [
            {
              url: "http://192.168.1.115:8000/media/2025/09/16/test-image.png",
              file_name: "test-image.png",
              mime_type: "image/png",
              file_size: 1016127,
              is_image: true,
              is_video: false,
              is_document: false,
              caption: "Test image caption"
            }
          ],
          sender: {
            id: 137,
            name: "Test User",
            image: "http://192.168.1.115:8000/profile-images/test.png"
          },
          senderId: 137,
          timestamp: new Date().toISOString()
        };

        // Add the test message to the current conversation or first available chat
        if (selectedChat) {
          addNewMessageToConversation(selectedChat, testMessageData);
        } else if (memoizedChats && memoizedChats.length > 0) {
          const firstChat = memoizedChats[0];
          addNewMessageToConversation(firstChat.id, testMessageData);
        } else {
        }
      };

      // Test function to simulate notification event (exact format from notification system)
      window.testNotificationMedia = () => {
        const testNotificationData = {
          message: "Check out this image!",
          sender: {
            id: 137,
            name: "Test User",
            image: "http://192.168.1.115:8000/profile-images/test.png"
          },
          senderId: 137,
          media: [
            {
              url: "http://192.168.1.115:8000/media/2025/09/16/notification-test.png",
              file_name: "notification-test.png",
              mime_type: "image/png",
              file_size: 500000,
              is_image: true,
              is_video: false,
              is_document: false
            }
          ],
          hasMedia: true,
          conversationId: selectedChat || 35,
          messageId: 999,
          timestamp: new Date().toISOString()
        };

        // Simulate the new-message-received event
        const messageEvent = new CustomEvent('new-message-received', {
          detail: testNotificationData
        });
        window.dispatchEvent(messageEvent);
      };

      // Debug function to check current messages
      window.debugMessages = () => {
        if (messages[selectedChat]) {
          messages[selectedChat].forEach((msg, index) => {
            if (msg.media && msg.media.length > 0) {
            }
          });
        }
      };

      // Function to check available chats
      window.checkChats = () => {
        if (memoizedChats && memoizedChats.length > 0) {
          memoizedChats.forEach((chat, index) => {
          });
        } else {
        }
      };

      // Function to select first chat for testing
      window.selectFirstChat = () => {
        if (memoizedChats && memoizedChats.length > 0) {
          const firstChat = memoizedChats[0];
          setSelectedChat(firstChat.id);
        } else {
        }
      };
    }
  }, []);

  // Transform API message response to match expected format
  const transformMessageData = (apiMessage) => {
    // Helper function to detect if a media item is audio
    const isAudioMediaItem = (item) => {
      // Check explicit flags first (most reliable)
      if (item.is_audio || item.isAudio || item.file_type === 'audio') {
        return true;
      }

      // Check mime type
      const mimeType = item.mime_type || item.type;
      if (mimeType && typeof mimeType === 'string' && mimeType.startsWith('audio/')) {
        return true;
      }

      // Check URL extension for common audio formats
      const url = item.url || '';
      if (typeof url === 'string') {
        const audioExtensions = ['.webm', '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.opus'];
        const lowerUrl = url.toLowerCase();
        if (audioExtensions.some(ext => lowerUrl.includes(ext))) {
          return true;
        }
      }

      return false;
    };

    // Check if message is audio type - prioritize message_type from API
    const isAudio = apiMessage.message_type === 'audio' ||
      apiMessage.is_audio ||
      apiMessage.isAudio ||
      (apiMessage.media && apiMessage.media.some(isAudioMediaItem));

    // Transform media objects to match expected format
    const transformedMedia = apiMessage.media ? apiMessage.media.map(mediaItem => {
      const isAudio = isAudioMediaItem(mediaItem);
      let mimeType = mediaItem.mime_type || mediaItem.type;
      const fileType = mediaItem.file_type || mediaItem.type;

      // If it's audio but mime type is video/webm (common for webm audio), convert to audio/webm
      if (isAudio && mimeType && mimeType.startsWith('video/')) {
        // Check if URL suggests it's audio (webm audio files)
        const url = mediaItem.url || '';
        if (url.toLowerCase().includes('.webm') || fileType === 'audio') {
          mimeType = 'audio/webm';
        }
      }

      // If no mime type but we know it's audio, set appropriate audio mime type
      if (isAudio && !mimeType) {
        const url = mediaItem.url || '';
        if (url.toLowerCase().includes('.mp3')) {
          mimeType = 'audio/mpeg';
        } else if (url.toLowerCase().includes('.wav')) {
          mimeType = 'audio/wav';
        } else if (url.toLowerCase().includes('.ogg')) {
          mimeType = 'audio/ogg';
        } else {
          mimeType = 'audio/webm'; // Default for webm audio
        }
      }

      return {
        url: mediaItem.url,
        type: mimeType || (isAudio ? 'audio/webm' : fileType),
        name: mediaItem.file_name || mediaItem.name,
        size: mediaItem.file_size || mediaItem.size,
        isImage: mediaItem.is_image || (mimeType && mimeType.startsWith('image/')),
        isVideo: mediaItem.is_video || (mimeType && mimeType.startsWith('video/') && !isAudio),
        isDocument: mediaItem.is_document || (!isAudio && !mediaItem.is_image && !mediaItem.is_video &&
          !(mimeType && (mimeType.startsWith('image/') || mimeType.startsWith('video/') || mimeType.startsWith('audio/')))),
        isAudio: isAudio,
        duration: mediaItem.duration || null
      };
    }) : null

    // Ensure user object has profile_image_url, handle empty strings and invalid values
    let userProfileImage = apiMessage.user?.profile_image_url;

    // Normalize the value - convert null/undefined to null, trim strings
    if (typeof userProfileImage === 'string') {
      userProfileImage = userProfileImage.trim();
      if (userProfileImage === '' || userProfileImage.toLowerCase() === 'null' || userProfileImage.toLowerCase() === 'undefined') {
        userProfileImage = null;
      }
    } else if (!userProfileImage || userProfileImage === null || userProfileImage === undefined) {
      userProfileImage = null;
    }

    // For own messages, if profile_image_url is missing, get it from localStorage
    if (apiMessage.is_own_message && !userProfileImage) {
      try {
        const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
        const storedProfileImage = userData.profile_image_url;
        if (storedProfileImage && typeof storedProfileImage === 'string') {
          const trimmed = storedProfileImage.trim();
          if (trimmed !== '' && trimmed.toLowerCase() !== 'null' && trimmed.toLowerCase() !== 'undefined') {
            userProfileImage = trimmed;
          }
        }
      } catch (e) {
        console.warn('Failed to get user profile image from localStorage:', e);
      }
    }

    // Final validation - ensure we have a valid URL/path string
    const validProfileImage = (userProfileImage &&
      typeof userProfileImage === 'string' &&
      userProfileImage !== '' &&
      userProfileImage.toLowerCase() !== 'null' &&
      userProfileImage.toLowerCase() !== 'undefined' &&
      !userProfileImage.startsWith('null') &&
      !userProfileImage.startsWith('undefined') &&
      !userProfileImage.includes('null/') &&
      !userProfileImage.includes('undefined/'))
      ? userProfileImage
      : null;

    // Determine if message is from current user
    // Check is_own_message flag first, then fall back to user ID comparison
    let isOwnMessage = apiMessage.is_own_message;

    // If is_own_message is missing, determine it by comparing user IDs
    if (isOwnMessage === undefined || isOwnMessage === null) {
      try {
        const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
        const currentUserId = userData.user_id || userData.id;
        const messageUserId = apiMessage.user?.user_id || apiMessage.user?.id;

        if (currentUserId && messageUserId) {
          isOwnMessage = String(currentUserId) === String(messageUserId);
        }
      } catch (e) {
        // If we can't determine, default to false
        isOwnMessage = false;
      }
    }

    // Determine message type - prioritize message_type from API, then check audio/media
    let messageType = "text";

    // Use message_type from API if available (most reliable)
    if (apiMessage.message_type) {
      messageType = apiMessage.message_type;
    } else if (isAudio) {
      // Fallback: detect audio from media items
      messageType = "audio";
    } else if (apiMessage.has_media || (apiMessage.media && apiMessage.media.length > 0)) {
      messageType = "media";
    }

    // Extract duration if available (from message or media)
    const duration = apiMessage.duration ||
      (transformedMedia && transformedMedia[0]?.duration) ||
      null;

    return {
      id: apiMessage.id,
      senderId: isOwnMessage ? "me" : (apiMessage.user?.id || apiMessage.user_id),
      senderName: isOwnMessage ? "You" : `${apiMessage.user?.first_name || ""} ${apiMessage.user?.last_name || ""}`.trim(),
      content: apiMessage.content,
      timestamp: formatTimestamp(apiMessage.created_at),
      originalTimestamp: apiMessage.created_at, // Store original timestamp for sorting
      type: messageType,
      user: {
        ...apiMessage.user,
        profile_image_url: validProfileImage || "/icons/person.png"
      },
      isOwnMessage: isOwnMessage,
      isRead: apiMessage.read_at || false, // Add read status
      media: transformedMedia, // Add transformed media array if present
      duration: duration, // Add duration for audio messages
    }
  }

  // Load messages for a conversation
  const loadMessages = async (conversationId, page = 1, limit = 20) => {
    // Prevent duplicate calls for the same conversation
    if (loadingConversations.has(conversationId)) {
      return
    }

    try {
      // Mark conversation as loading
      setLoadingConversations(prev => new Set(prev).add(conversationId))

      if (page === 1) {
        setMessagesLoading(true)
        setIsLoadingOlderMessages(false)
      } else {
        setLoadingOlderMessages(true)
        setIsLoadingOlderMessages(true)
      }
      setMessagesError(null)

      // Check if this is a temporary chat ID (starts with 'temp-')
      if (conversationId.toString().startsWith('temp-')) {
        setMessages([]);
        setHasMoreMessages(prev => ({ ...prev, [conversationId]: false }));
        setIsLoadingOlderMessages(false);
        return;
      }

      const response = await conversationsAPI.getMessages(conversationId, { page, limit })


      const transformedMessages = response.data.messages.map(transformMessageData)

      // Ensure messages are in chronological order (oldest first)
      const sortedMessages = transformedMessages.sort((a, b) => {
        const dateA = new Date(a.originalTimestamp)
        const dateB = new Date(b.originalTimestamp)
        return dateA - dateB
      })

      setMessages(prev => {
        const existingMessages = prev[conversationId] || []

        if (page === 1) {
          // First page - replace all messages
          isAddingNewMessageRef.current = true
          return {
            ...prev,
            [conversationId]: sortedMessages
          }
        } else {
          // Subsequent pages - prepend older messages
          isAddingNewMessageRef.current = false
          return {
            ...prev,
            [conversationId]: [...sortedMessages, ...existingMessages]
          }
        }
      })

      // Check if there are more messages to load using pagination data
      const hasMore = response.data.pagination && response.data.pagination.current_page < response.data.pagination.last_page
      setHasMoreMessages(prev => ({
        ...prev,
        [conversationId]: hasMore
      }))

    } catch (err) {
      console.error('❌ Error loading messages:', err)
      let errorMessage = t("chat.errors.loadMessages", { defaultValue: "Failed to load messages. Please try again." })

      if (err.response) {
        errorMessage = err.response.data?.message || t("chat.errors.server", { code: err.response.status, defaultValue: "Server error: {{code}}" })
      } else if (err.request) {
        errorMessage = t("chat.errors.network", { defaultValue: "Network error. Please check your internet connection." })
      } else {
        errorMessage = err.message || errorMessage
      }

      setMessagesError(errorMessage)
    } finally {
      // Remove conversation from loading set
      setLoadingConversations(prev => {
        const newSet = new Set(prev)
        newSet.delete(conversationId)
        return newSet
      })

      if (page === 1) {
        setMessagesLoading(false)
        setIsLoadingOlderMessages(false)
      } else {
        setLoadingOlderMessages(false)
        setIsLoadingOlderMessages(false)
      }
    }
  }

  // Get current messages directly without memoization to prevent re-rendering issues
  const currentMessages = messages[selectedChat] || [];

  // Get current chat directly to prevent re-rendering issues
  const currentChat = memoizedChats.find((chat) => chat.id === selectedChat);

  // Separate new messages for smooth rendering
  const currentNewMessages = newMessages[selectedChat] || [];

  // Create a stable message list that doesn't cause re-renders
  const stableMessageList = useMemo(() => {
    // If no new messages, return current messages directly
    if (currentNewMessages.length === 0) {
      return currentMessages;
    }

    // Combine messages and sort by timestamp
    const combined = [...currentMessages, ...currentNewMessages];
    return combined.sort((a, b) => {
      const timeA = new Date(a.originalTimestamp || a.timestamp).getTime();
      const timeB = new Date(b.originalTimestamp || b.timestamp).getTime();
      return timeA - timeB;
    });
  }, [currentMessages, currentNewMessages]);

  // Use the stable list for rendering
  const memoizedMessageList = stableMessageList;

  // Helper functions that need to be defined before useMemo
  const getFileIcon = (fileType) => {
    if (!fileType) return '📄'
    if (fileType.startsWith('image/')) return '🖼️'
    if (fileType.startsWith('video/')) return '🎥'
    if (fileType.startsWith('audio/')) return '🎵'
    return '📄'
  }

  const openMediaModal = (media) => {
    setSelectedMedia(media)
    setShowMediaModal(true)
  }

  // Memoized messages container to prevent re-renders
  const MemoizedMessagesContainer = useMemo(() => {
    return memoizedMessageList.length === 0 ? (
      <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <p style={{ color: "#666", fontSize: "14px" }}>No messages yet. Start the conversation!</p>
      </div>
    ) : (
      <>
        {/* Render messages with stable keys and React.memo optimization */}
        {memoizedMessageList.map((msg, index) => (
          <MessageBubble
            key={msg.id || `temp-${index}`}
            message={msg}
            currentChat={currentChat}
            openMediaModal={openMediaModal}
            getFileIcon={getFileIcon}
          />
        ))}
      </>
    );
  }, [memoizedMessageList, currentChat, openMediaModal, getFileIcon]);

  // Clear new messages when conversation changes and merge them with regular messages
  // Also refresh messages from server to ensure we have the latest state
  useEffect(() => {
    if (selectedChat) {
      // Store active conversation ID in localStorage for notification handling
      localStorage.setItem('active_conversation_id', selectedChat.toString());

      // Get new messages for this conversation (received via WebSocket while viewing other conversations)
      const newMessagesForChat = newMessages[selectedChat] || [];

      // Always refresh messages from server when switching conversations
      // This ensures we have the latest state including any messages received via WebSocket

      // Load messages from server immediately (not debounced) to get latest state
      // This will include any messages received via WebSocket while viewing other conversations
      loadMessages(selectedChat, 1);

      // If there are new messages from WebSocket, merge them after a short delay
      // to ensure server messages are loaded first
      if (newMessagesForChat.length > 0) {
        setTimeout(() => {
          setMessages(prev => {
            const currentMessages = prev[selectedChat] || [];
            // Merge new WebSocket messages, avoiding duplicates
            const existingIds = new Set(currentMessages.map(msg => msg.id));
            const uniqueNewMessages = newMessagesForChat.filter(msg => !existingIds.has(msg.id));

            if (uniqueNewMessages.length > 0) {
              // Combine and sort by timestamp
              const combined = [...currentMessages, ...uniqueNewMessages];
              const sorted = combined.sort((a, b) => {
                const timeA = new Date(a.originalTimestamp || a.timestamp).getTime();
                const timeB = new Date(b.originalTimestamp || b.timestamp).getTime();
                return timeA - timeB;
              });

              return {
                ...prev,
                [selectedChat]: sorted
              };
            }
            return prev;
          });

          // Clear new messages for this conversation
          setNewMessages(prev => {
            const { [selectedChat]: removed, ...rest } = prev;
            return rest;
          });
        }, 500); // Wait for server messages to load
      } else {
        // Clear new messages even if empty to clean up state
        setNewMessages(prev => {
          const { [selectedChat]: removed, ...rest } = prev;
          return rest;
        });
      }
    } else {
      // Clear active conversation ID when no chat is selected
      localStorage.removeItem('active_conversation_id');
    }
    // Note: loadMessages is stable and doesn't need to be in dependencies
    // We only want to refresh when selectedChat changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat]); // Removed newMessages dependency to prevent infinite loop

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      try {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      } catch (e) {
        // fallback if scroll not supported
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }
  }, []);




  // Ultra-optimized function to add new message without ANY re-renders

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get("user");
    const pathSegments = window.location.pathname.split("/");

    let extractedConversationId = null;
    let extractedConversationType = null;

    // Priority 1: If receiverId exists, handle it first and directly
    if (receiverId) {
      handleChatWithReceiver(receiverId, "medical");
      return; // Exit early to prevent other logic from running
    } else if (pathSegments.includes("chat") && pathSegments.length === 2 && pathSegments[1] === "chat") {
      setSelectedChat(null);
    } else if (extractedConversationId && extractedConversationType) {
      // Only handle medical conversations
      if (extractedConversationType === "medical") {
        const targetChat = memoizedChats.find((chat) => chat.id.toString() === extractedConversationId.toString());
        if (targetChat) {
          setSelectedChat(parseInt(extractedConversationId));
        } else {
          handleChatWithReceiver(extractedConversationId, "medical");
        }
      } else {
      }
    } else if (userId) {
      const existingChat = memoizedChats.find(
        (chat) => chat.id.toString() === userId || chat.name.toLowerCase().includes(userId)
      );
      if (existingChat) {
        setSelectedChat(existingChat.id);
      } else if (memoizedChats.length > 0 && activeTab !== "social") {
        setSelectedChat(memoizedChats[0].id);
      }
    } else if (conversationId) {
      const targetChat = memoizedChats.find((chat) => chat.id.toString() === conversationId.toString());
      if (targetChat) {
        setSelectedChat(parseInt(conversationId));
      } else {
      }
    } else if (initialChatId) {
      setSelectedChat(initialChatId);
    } else if (memoizedChats.length > 0 && !selectedChat && pathSegments.length !== 2 && !(pathSegments.length === 2 && pathSegments[1] === "chat")) {
      // Auto-select first medical chat
      const medicalChats = memoizedChats;
      if (medicalChats.length > 0) {
        setSelectedChat(medicalChats[0].id);
      } else {
      }
    }
  }, [initialChatId, activeTab, conversationId, receiverId]); // Added receiverId to dependencies
  // Handle chat with specific receiver (always medical)
  const handleChatWithReceiver = async (receiverId, conversationType = "medical") => {
    if (conversationLoading) {
      return;
    }

    // Check if the conversation already exists in chats
    const existingChat = memoizedChats.find((chat) => chat.otherParticipant?.id?.toString() === receiverId.toString());
    if (existingChat) {
      setSelectedChat(existingChat.id);
      // Load messages if not already loaded
      if (!messages[existingChat.id] || messages[existingChat.id].length === 0) {
        loadMessages(existingChat.id, 1);
      }
      return;
    }

    try {
      setConversationLoading(true);
      const response = await conversationsAPI.getConversationByReceiver(receiverId, conversationType);

      if (response.data) {
        let conversation = null;
        if (Array.isArray(response.data) && response.data.length > 0) {
          conversation = response.data[0];
        } else if (!Array.isArray(response.data) && response.data.id) {
          conversation = response.data;
        }

        if (conversation) {
          const transformedChat = await transformReceiverConversationData(conversation, receiverId, conversationType);
          if (transformedChat) {
            setChats((prev) => {
              const existingChat = prev.find((chat) => chat.id === conversation.id);
              if (!existingChat) {
                return [...prev, transformedChat];
              }
              return prev;
            });

            setChatUpdates((prev) => {
              const { [conversation.id]: removed, ...rest } = prev;
              return rest;
            });

            setSelectedChat(conversation.id);
          }
        }
        window.history.pushState({}, "", "/medical/clinic/chat");
      } else {
        window.history.pushState({}, "", "/medical/clinic/chat");
      }
    } catch (err) {
      // Check for 403 Forbidden - Security restriction for medical chats
      if (err.response && err.response.status === 403 && conversationType === 'medical') {
        if (window.showErrorToast) {
          window.showErrorToast(t("chat.errors.consultationRequired", { defaultValue: "Access Denied: Medical chats require an active SOS case." }));
        }
      }

      console.error("❌ Error getting conversation for receiver:", err);
      window.history.pushState({}, "", "/medical/clinic/chat");
      if (chats.length > 0) {
        setSelectedChat(chats[0].id);
      }
    } finally {
      setConversationLoading(false);
    }
  };

  // Transform receiver conversation data to match chat format
  const transformReceiverConversationData = async (conversation, receiverId, conversationType) => {
    // Find the receiver in participants (not the current user)
    const receiver = conversation.participants.find(participant =>
      participant.id.toString() === receiverId.toString()
    )

    if (!receiver) {
      console.warn('Receiver not found in conversation participants:', receiverId)
      return null
    }

    // Load messages for this conversation using the same approach as loadMessages
    try {
      // Check if messages are already being loaded for this conversation
      if (loadingConversations.has(conversation.id)) {
        // Return basic conversation data without messages
        return {
          id: conversation.id,
          name: `${receiver.first_name} ${receiver.last_name}`,
          avatar: receiver.profile_image_url || "/icons/person.png",
          lastMessage: t("chat.list.noMessages"),
          timestamp: formatTimestamp(conversation.updated_at),
          unreadCount: 0,
          isOnline: false,
          userType: "user",
          type: "medical",
          otherParticipant: receiver,
        }
      }

      // Mark conversation as loading
      setLoadingConversations(prev => new Set(prev).add(conversation.id))

      // Check if this is a temporary chat ID (starts with 'temp-')
      if (conversation.id.toString().startsWith('temp-')) {
        setLoadingConversations(prev => {
          const newSet = new Set(prev);
          newSet.delete(conversation.id);
          return newSet;
        });
        return;
      }

      const response = await conversationsAPI.getMessages(conversation.id, { page: 1, limit: 20 })

      const transformedMessages = response.data.messages.map(transformMessageData)

      // Ensure messages are in chronological order (oldest first)
      const sortedMessages = transformedMessages.sort((a, b) => {
        const dateA = new Date(a.originalTimestamp)
        const dateB = new Date(b.originalTimestamp)
        return dateA - dateB
      })

      // Store messages in the messages state
      setMessages(prev => ({
        ...prev,
        [conversation.id]: sortedMessages
      }))

      // Check if there are more messages to load using pagination data
      const hasMore = response.data.pagination && response.data.pagination.current_page < response.data.pagination.last_page
      setHasMoreMessages(prev => ({
        ...prev,
        [conversation.id]: hasMore
      }))

      // Set initial page
      setCurrentPages(prev => ({
        ...prev,
        [conversation.id]: 1
      }))

      // Get the latest message for chat display
      const latestMessage = sortedMessages.length > 0 ? sortedMessages[sortedMessages.length - 1] : null

      return {
        id: conversation.id,
        name: `${receiver.first_name} ${receiver.last_name}`,
        avatar: receiver.profile_image_url || "/icons/person.png",
        lastMessage: latestMessage ? latestMessage.content : t("chat.list.noMessages"),
        timestamp: latestMessage ? latestMessage.timestamp : formatTimestamp(conversation.updated_at),
        unreadCount: 0, // We'll need to get this from the API if available
        isOnline: false,
        userType: "user",
        type: conversationType || "all", // Preserve conversation type from API
        otherParticipant: receiver,
      }
    } catch (err) {
      console.error('❌ Error loading messages for receiver conversation:', err)

      // Return basic conversation data even if messages fail to load
      return {
        id: conversation.id,
        name: `${receiver.first_name} ${receiver.last_name}`,
        avatar: receiver.profile_image_url || "/icons/person.png",
        lastMessage: t("chat.list.noMessages"),
        timestamp: formatTimestamp(conversation.updated_at),
        unreadCount: 0,
        isOnline: false,
        userType: "user",
        type: conversationType || "all", // Preserve conversation type from API
        otherParticipant: receiver,
      }
    } finally {
      // Remove conversation from loading set
      setLoadingConversations(prev => {
        const newSet = new Set(prev)
        newSet.delete(conversation.id)
        return newSet
      })
    }
  }

  const debouncedLoadMessages = debounce((conversationId, page, limit) => {
    loadMessages(conversationId, page, limit);
  }, 300);

  // UseEffect for loading messages (only if not already loading from conversation change)
  // Note: Messages are now refreshed when conversation changes in the useEffect above
  useEffect(() => {
    if (selectedChat) {
      // Only load if messages haven't been loaded yet (initial load)
      // Conversation changes are handled by the useEffect above which always refreshes
      if (!messages[selectedChat] || messages[selectedChat].length === 0) {
        debouncedLoadMessages(selectedChat, 1);
      }

      // Mark conversation as read
      markConversationAsRead(selectedChat);
    }
  }, [selectedChat]);
  // Track current page for each conversation
  const [currentPages, setCurrentPages] = useState({})

  // Loading states to prevent duplicate API calls
  const [loadingConversations, setLoadingConversations] = useState(new Set())
  const [conversationLoading, setConversationLoading] = useState(false)

  // Load older messages when user scrolls to top
  const loadOlderMessages = async () => {
    if (!selectedChat || loadingOlderMessages || !hasMoreMessages[selectedChat]) {
      return
    }

    const currentPage = currentPages[selectedChat] || 1
    const nextPage = currentPage + 1

    const messagesBeforeLoad = messages[selectedChat]?.length || 0

    try {
      await loadMessages(selectedChat, nextPage)
      setCurrentPages(prev => ({
        ...prev,
        [selectedChat]: nextPage
      }))

      // Scroll to maintain position after loading older messages
      setTimeout(() => {
        const messagesAfterLoad = messages[selectedChat]?.length || 0
        const newMessagesCount = messagesAfterLoad - messagesBeforeLoad
        if (newMessagesCount > 0 && messagesContainerRef.current) {
          // Calculate scroll position to maintain user's view
          const container = messagesContainerRef.current
          const scrollHeight = container.scrollHeight
          const newScrollTop = scrollHeight - container.clientHeight
          container.scrollTop = newScrollTop
        }
      }, 100)

    } catch (error) {
      console.error('❌ Error loading older messages:', error)
    }
  }


  // Mark conversation as read

  // Mark specific message as read (for immediate marking of new messages)
  const markMessageAsRead = async (conversationId, messageId) => {
    try {
      await conversationsAPI.markAsRead(conversationId, { message_id: messageId })

      // Update the specific message's read status
      setMessages(prev => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).map(msg =>
          msg.id === messageId ? { ...msg, isRead: true } : msg
        )
      }))
    } catch (err) {
      console.error('❌ Error marking message as read:', err)
    }
  }

  useEffect(() => {
    // Only scroll to bottom when we're adding new messages (not loading older ones)
    if (isAddingNewMessageRef.current) {
      // scrollToBottom()
      // Reset the flag after scrolling
      setTimeout(() => {
        isAddingNewMessageRef.current = false
      }, 100)
    }
  }, [currentMessages])

  const handleSendMessage = async () => {
    const messageValue = messageInputText;
    if ((messageValue.trim() || selectedFiles.length > 0) && selectedChat) {
      try {
        // Get current user data for profile image
        const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
        const currentUserProfileImage = userData.profile_image_url || "/icons/person.png";

        // Create optimistic message immediately
        const optimisticMessage = {
          id: `temp-${Date.now()}`, // Temporary ID
          senderId: "me",
          senderName: "You",
          content: messageValue,
          timestamp: formatTimestamp(new Date()),
          originalTimestamp: new Date().toISOString(),
          type: selectedFiles.length > 0 ? "media" : "text",
          user: {
            id: "me",
            first_name: "You",
            last_name: "",
            profile_image_url: currentUserProfileImage
          },
          isOwnMessage: true,
          isRead: false,
          isSending: false, // Mark as sending
          media: selectedFiles.length > 0 ? selectedFiles.map(file => ({
            url: URL.createObjectURL(file),
            type: file.type,
            name: file.name,
            size: file.size,
            isImage: file.type.startsWith('image/'),
            isVideo: file.type.startsWith('video/'),
            isDocument: !file.type.startsWith('image/') && !file.type.startsWith('video/')
          })) : null
        }

        // Add optimistic message immediately with duplicate prevention
        isAddingNewMessageRef.current = true
        setMessages(prev => {
          const currentMessages = prev[selectedChat] || [];
          // Check if optimistic message already exists
          const messageExists = currentMessages.some(msg => msg.id === optimisticMessage.id);
          if (messageExists) {
            return prev; // No change needed
          }
          return {
            ...prev,
            [selectedChat]: [...currentMessages, optimisticMessage]
          };
        })

        // Clear the input immediately
        if (messageInputRef.current) {
          messageInputRef.current.value = "";
        }
        setMessageInputText("") // Clear the input text state
        setSelectedFiles([])

        // Stop typing indicator when message is sent
        stopCurrentUserTyping()

        // Smart scroll to bottom for sent messages
        setTimeout(() => {
          scrollToBottomSmooth();
        }, 100);

        // Change URL from /chat/{receiverId} to /chat after sending message
        const currentPath = window.location.pathname
        if (currentPath.startsWith('/chat/') && currentPath !== '/chat') {
          window.history.pushState({}, "", "/medical/clinic/chat");
        }

        // Send to API in background
        let response
        if (selectedFiles.length > 0) {
          // Send message with media files
          response = await conversationsAPI.sendMessageWithMedia(selectedChat, messageValue, selectedFiles)
        } else {
          // Send text-only message
          response = await conversationsAPI.sendMessage(selectedChat, {
            content: messageValue
          })
        }


        // Update conversation list - move current conversation to top
        setChats(prev => {
          const currentChatIndex = prev.findIndex(chat => chat.id === selectedChat);
          if (currentChatIndex === -1) return prev; // Chat not found

          // Remove the current chat from its position
          const currentChat = prev[currentChatIndex];
          const otherChats = prev.filter((_, index) => index !== currentChatIndex);

          // Update the current chat with new message info
          const updatedCurrentChat = {
            ...currentChat,
            lastMessage: messageValue,
            timestamp: new Date().toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }),
            unreadCount: 0 // Reset unread count since we just sent a message
          };

          // Put current chat at the top
          return [updatedCurrentChat, ...otherChats];
        });

        // Keep the optimistic message as is - no updates needed
        // The message is already displayed and working perfectly

      } catch (err) {
        console.error("❌ Error sending message:", err)

        // Remove the optimistic message on error
        setMessages(prev => {
          const currentMessages = prev[selectedChat] || [];
          const updatedMessages = currentMessages.filter(msg => msg.id !== optimisticMessage.id);
          return {
            ...prev,
            [selectedChat]: updatedMessages
          };
        })

        alert("Failed to send message. Please try again.")
      } finally {
      }
    }
  }

  const handleKeyPress = (e) => {
    // Only allow Enter key if no media is selected
    if (e.key === "Enter" && !e.shiftKey && selectedFiles.length === 0) {
      e.preventDefault()
      handleSendMessage()
      scrollToBottom();
    }
  }

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files)
    setSelectedFiles(prev => [...prev, ...files])
    event.target.value = '' // Reset input
  }

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const closeMediaModal = () => {
    setShowMediaModal(false)
    setSelectedMedia(null)
  }

  // Memoize mic button color
  const micButtonColor = useMemo(() => {
    return isRecording ? "#EF4444" : "#999999";
  }, [isRecording]);

  // Start audio recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);

      // Create AudioContext for waveform visualization
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtx.state === 'suspended') {
        try {
          await audioCtx.resume();
        } catch (resumeError) {
          console.warn('Recording audio context resume failed:', resumeError);
        }
      }
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      setAudioContext(audioCtx);
      setAnalyserNode(analyser);

      // Try to find a supported mime type
      let mimeType = 'audio/webm;codecs=opus';
      const options = { mimeType };

      if (!MediaRecorder.isTypeSupported(mimeType)) {
        // Try fallback options
        const fallbackTypes = [
          'audio/webm',
          'audio/mp4',
          'audio/ogg;codecs=opus',
          'audio/wav'
        ];

        for (const type of fallbackTypes) {
          if (MediaRecorder.isTypeSupported(type)) {
            mimeType = type;
            options.mimeType = type;
            break;
          }
        }
      }

      const recorder = new MediaRecorder(stream, options);

      const chunks = [];
      const finalMimeType = mimeType; // Store mimeType for use in callback

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        // Determine file extension based on mime type
        let extension = 'webm';
        if (finalMimeType.includes('mp4')) extension = 'mp4';
        else if (finalMimeType.includes('ogg')) extension = 'ogg';
        else if (finalMimeType.includes('wav')) extension = 'wav';

        const audioBlob = new Blob(chunks, { type: finalMimeType });
        const audioUrl = URL.createObjectURL(audioBlob);

        // Create a File object from the blob
        const audioFile = new File([audioBlob], `voice-message-${Date.now()}.${extension}`, {
          type: finalMimeType
        });

        setRecordedAudio({
          blob: audioBlob,
          url: audioUrl,
          file: audioFile,
          duration: recordingDuration,
          mimeType: finalMimeType
        });

        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.onerror = (event) => {
        console.error('Recording error:', event.error);
        stopRecording();
        alert('Error recording audio. Please try again.');
      };

      recorder.start();
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      setIsRecording(true);
      setRecordingDuration(0);

      // Start timer for recording duration
      const timer = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      setRecordingTimer(timer);

    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }

    if (recordingTimer) {
      clearInterval(recordingTimer);
      setRecordingTimer(null);
    }

    // Clean up audio context
    if (audioContext) {
      audioContext.close();
      setAudioContext(null);
    }
    setAnalyserNode(null);

    // Stop audio stream tracks
    if (audioStream) {
      audioStream.getTracks().forEach(track => {
        if (track.readyState === 'live') {
          track.stop();
        }
      });
      setAudioStream(null);
    }

    setIsRecording(false);
    setMediaRecorder(null);
    setAudioChunks([]);
    setRecordingDuration(0);

  };

  // Cancel recording
  const cancelRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }

    if (recordingTimer) {
      clearInterval(recordingTimer);
      setRecordingTimer(null);
    }

    // Clean up audio context
    if (audioContext) {
      audioContext.close();
      setAudioContext(null);
    }
    setAnalyserNode(null);

    // Stop all tracks if any are still active
    if (mediaRecorder && mediaRecorder.stream) {
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }

    if (audioStream) {
      audioStream.getTracks().forEach(track => {
        if (track.readyState === 'live') {
          track.stop();
        }
      });
      setAudioStream(null);
    }

    setIsRecording(false);
    setMediaRecorder(null);
    setAudioChunks([]);
    setRecordingDuration(0);
    setRecordedAudio(null);

  };

  // Send recorded audio
  const sendRecordedAudio = async () => {
    if (!recordedAudio || !selectedChat) {
      return;
    }

    try {
      setIsSending(true);

      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      const rawProfileImage = userData.profile_image_url;
      const currentUserProfileImage = (rawProfileImage &&
        rawProfileImage !== '' &&
        rawProfileImage !== 'null' &&
        rawProfileImage !== 'undefined')
        ? rawProfileImage
        : "/icons/person.png";

      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        senderId: "me",
        senderName: t("chat.messages.you"),
        content: t("chat.messages.voiceMessage"),
        timestamp: formatTimestamp(new Date()),
        originalTimestamp: new Date().toISOString(),
        type: "audio",
        user: {
          id: "me",
          first_name: t("chat.messages.you"),
          last_name: "",
          profile_image_url: currentUserProfileImage
        },
        isOwnMessage: true,
        isRead: false,
        isSending: true,
        media: [{
          url: recordedAudio.url,
          type: recordedAudio.mimeType || 'audio/webm',
          name: recordedAudio.file.name,
          size: recordedAudio.blob.size,
          isImage: false,
          isVideo: false,
          isDocument: false,
          isAudio: true
        }]
      };

      isAddingNewMessageRef.current = true;
      setMessages(prev => {
        const currentMessages = prev[selectedChat] || [];
        const messageExists = currentMessages.some(msg => msg.id === optimisticMessage.id);
        if (messageExists) {
          return prev;
        }
        return {
          ...prev,
          [selectedChat]: [...currentMessages, optimisticMessage]
        };
      });

      setTimeout(() => {
        scrollToBottomSmooth();
      }, 100);

      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/medical/clinic/chat/') && currentPath !== '/medical/clinic/chat') {
        window.history.pushState({}, '', '/medical/clinic/chat');
      }

      // Send audio file
      const response = await conversationsAPI.sendMessageWithMedia(selectedChat, "Voice message", [recordedAudio.file]);


      const realMessage = {
        id: response.data.id,
        senderId: "me",
        senderName: t("chat.messages.you"),
        content: response.data.content || t("chat.messages.voiceMessage"),
        timestamp: formatTimestamp(response.data.created_at),
        originalTimestamp: response.data.created_at,
        type: "audio",
        user: {
          id: "me",
          first_name: t("chat.messages.you"),
          last_name: "",
          profile_image_url: (response.data.user?.profile_image_url &&
            response.data.user.profile_image_url !== '' &&
            response.data.user.profile_image_url !== 'null' &&
            response.data.user.profile_image_url !== 'undefined')
            ? response.data.user.profile_image_url
            : currentUserProfileImage
        },
        isOwnMessage: true,
        isRead: false,
        isSending: false,
        media: response.data.media ? response.data.media.map(mediaItem => ({
          url: mediaItem.url,
          type: mediaItem.mime_type,
          name: mediaItem.file_name,
          size: mediaItem.file_size,
          isImage: mediaItem.is_image,
          isVideo: mediaItem.is_video,
          isDocument: mediaItem.is_document,
          isAudio: mediaItem.is_audio || (mediaItem.mime_type && mediaItem.mime_type.startsWith('audio/'))
        })) : null
      };

      setMessages(prev => {
        const currentMessages = prev[selectedChat] || [];
        const optimisticIndex = currentMessages.findIndex(msg => msg.id === optimisticMessage.id);

        if (optimisticIndex === -1) {
          return {
            ...prev,
            [selectedChat]: [...currentMessages, realMessage]
          };
        }

        const updatedMessages = [...currentMessages];
        updatedMessages[optimisticIndex] = realMessage;
        return {
          ...prev,
          [selectedChat]: updatedMessages
        };
      });

      setChats(prev => {
        const currentChatIndex = prev.findIndex(chat => chat.id === selectedChat);
        if (currentChatIndex === -1) return prev;

        const currentChat = prev[currentChatIndex];
        const otherChats = prev.filter((_, index) => index !== currentChatIndex);

        const updatedCurrentChat = {
          ...currentChat,
          lastMessage: t("chat.messages.voiceMessage"),
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }),
          unreadCount: 0
        };

        return [updatedCurrentChat, ...otherChats];
      });

      // Clean up recorded audio
      if (recordedAudio.url) {
        URL.revokeObjectURL(recordedAudio.url);
      }
      setRecordedAudio(null);

    } catch (err) {
      console.error("❌ Error sending audio message:", err);
      alert(t("chat.errors.sendVoice", { defaultValue: "Failed to send voice message. Please try again." }));
    } finally {
      setIsSending(false);
    }
  };

  // Format recording duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Insert emoji into message input at cursor position
  const insertEmoji = (emoji) => {
    const input = messageInputRef.current;
    if (!input) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const text = messageInputText;
    const newText = text.substring(0, start) + emoji + text.substring(end);

    setMessageInputText(newText);

    // Set cursor position after inserted emoji
    setTimeout(() => {
      input.focus();
      const newPosition = start + emoji.length;
      input.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  // Start camera
  const startCamera = async () => {
    try {
      setCameraLoading(true);

      // Stop any existing stream first
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }

      // Try different constraints
      const constraints = [
        {
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: cameraMode === 'video'
        },
        {
          video: {
            facingMode: 'user'
          },
          audio: cameraMode === 'video'
        },
        {
          video: true,
          audio: cameraMode === 'video'
        }
      ];

      let stream = null;
      let lastError = null;

      for (const constraint of constraints) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraint);
          break;
        } catch (error) {
          console.warn('Failed with constraints:', constraint, error);
          lastError = error;
          continue;
        }
      }

      if (!stream) {
        throw lastError || new Error('Failed to access camera');
      }

      // Log stream tracks for debugging
      const videoTracks = stream.getVideoTracks();
      const audioTracks = stream.getAudioTracks();


      // Warn if audio is missing in video mode
      if (cameraMode === 'video' && audioTracks.length === 0) {
        console.warn('📷 Warning: Video mode requested but no audio track available');
      }

      setCameraStream(stream);
      setIsCameraOpen(true);
      setCameraLoading(false);

    } catch (error) {
      console.error('Error starting camera:', error);
      setCameraLoading(false);
      setIsCameraOpen(false);

      let errorMessage = 'Could not access camera. ';
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage += 'Please allow camera access in your browser settings.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage += 'No camera found on your device.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage += 'Camera is being used by another application.';
      } else {
        errorMessage += 'Please check your camera permissions.';
      }

      alert(errorMessage);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
      cameraVideoRef.current.pause();
    }
    setIsCameraOpen(false);
    setIsRecordingVideo(false);
    setVideoRecorder(null);
    setVideoChunks([]);
    setCameraLoading(false);
  };

  // Capture photo
  const capturePhoto = () => {
    if (!cameraVideoRef.current || !cameraCanvasRef.current) {
      return;
    }

    const video = cameraVideoRef.current;
    const canvas = cameraCanvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setSelectedFiles(prev => [...prev, file]);
        stopCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  // Start video recording
  const startVideoRecording = () => {
    if (!cameraStream) {
      console.error('Cannot start recording: No camera stream available');
      alert('Camera stream not available. Please start the camera first.');
      return;
    }

    // Verify stream has video tracks
    const videoTracks = cameraStream.getVideoTracks();
    if (videoTracks.length === 0) {
      console.error('Cannot start recording: No video tracks in stream');
      alert('No video track available. Please check your camera.');
      return;
    }

    // Check if video track is active
    if (videoTracks[0].readyState !== 'live') {
      console.error('Cannot start recording: Video track is not live');
      alert('Video track is not active. Please restart the camera.');
      return;
    }

    try {
      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        alert('MediaRecorder is not supported in your browser.');
        return;
      }

      // Try different mime types in order of preference
      let mimeType = null;
      const preferredTypes = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4',
        'video/ogg;codecs=theora,opus',
        'video/ogg'
      ];

      for (const type of preferredTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }

      if (!mimeType) {
        // Fallback: use default (browser will choose)
        mimeType = '';
        console.warn('📹 No preferred mime type supported, using browser default');
      }

      const recorder = new MediaRecorder(cameraStream, {
        mimeType: mimeType || undefined,
        videoBitsPerSecond: 2500000 // 2.5 Mbps
      });

      const chunks = [];
      setVideoChunks([]); // Reset chunks

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
          // Also update state for tracking
          setVideoChunks(prev => [...prev, event.data]);
        }
      };

      recorder.onstop = () => {

        if (chunks.length === 0) {
          console.error('📹 No video chunks collected');
          alert('No video data was recorded. Please try again.');
          setIsRecordingVideo(false);
          setVideoRecorder(null);
          setVideoChunks([]);
          return;
        }

        // Determine final mime type and extension
        let finalMimeType = mimeType || 'video/webm';
        let extension = 'webm';

        if (finalMimeType.includes('mp4')) {
          extension = 'mp4';
        } else if (finalMimeType.includes('ogg')) {
          extension = 'ogg';
        }

        const blob = new Blob(chunks, { type: finalMimeType });

        if (blob.size === 0) {
          console.error('📹 Video blob is empty');
          alert('Recorded video is empty. Please try again.');
          setIsRecordingVideo(false);
          setVideoRecorder(null);
          setVideoChunks([]);
          return;
        }

        const file = new File([blob], `video-${Date.now()}.${extension}`, { type: finalMimeType });

        setSelectedFiles(prev => [...prev, file]);
        stopCamera();
      };

      recorder.onerror = (event) => {
        console.error('📹 Video recording error:', event.error || event);
        setIsRecordingVideo(false);
        setVideoRecorder(null);
        setVideoChunks([]);
        alert('Error recording video. Please try again.');
      };

      recorder.onstart = () => {
      };

      // Start recording with timeslice to collect data periodically
      // This ensures data is collected even if stop() is called quickly
      recorder.start(100); // Collect data every 100ms

      setVideoRecorder(recorder);
      setIsRecordingVideo(true);
    } catch (error) {
      console.error('📹 Error starting video recording:', error);
      setIsRecordingVideo(false);
      setVideoRecorder(null);
      setVideoChunks([]);
      alert(`Could not start video recording: ${error.message || 'Unknown error'}`);
    }
  };

  // Stop video recording
  const stopVideoRecording = () => {
    if (!videoRecorder) {
      console.warn('📹 No video recorder to stop');
      return;
    }

    if (videoRecorder.state === 'recording') {
      // Request any remaining data before stopping
      try {
        videoRecorder.requestData();
      } catch (error) {
        console.warn('📹 Error requesting data:', error);
      }

      // Stop the recorder (this will trigger onstop event)
      videoRecorder.stop();
    } else if (videoRecorder.state === 'inactive') {
      setIsRecordingVideo(false);
      setVideoRecorder(null);
      setVideoChunks([]);
    } else {
      try {
        videoRecorder.stop();
      } catch (error) {
        console.error('📹 Error stopping recorder:', error);
        setIsRecordingVideo(false);
        setVideoRecorder(null);
        setVideoChunks([]);
      }
    }
  };

  // Handle camera button click
  const handleCameraClick = () => {
    if (isCameraOpen) {
      stopCamera();
    } else {
      startCamera();
    }
  };

  // Set stream to video element when camera is open and stream is available
  useEffect(() => {
    if (isCameraOpen && cameraStream) {
      // Use setTimeout to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        if (cameraVideoRef.current && cameraStream) {
          const video = cameraVideoRef.current;

          video.srcObject = cameraStream;
          video.play().catch(err => {
            console.error('Error playing video:', err);
          });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isCameraOpen, cameraStream]);

  // Update camera mode and restart if needed
  useEffect(() => {
    if (isCameraOpen && cameraStream) {
      // If mode changed, restart camera with new mode
      startCamera();
    }
  }, [cameraMode]);

  // Cleanup effects for WebSocket, camera, and audio streams on unmount
  useEffect(() => {
    return () => {
      // Cleanup WebSocket subscriptions
      if (memoizedChats.length > 0) {
        memoizedChats.forEach((chat) => {
          const channelName = `conversation.${chat.id}`;
          try {
            unsubscribe(channelName);
          } catch (error) {
            console.error(`Error unsubscribing from channel ${channelName}:`, error);
          }
        });
      }

      // Cleanup camera stream
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      if (videoRecorder && videoRecorder.state !== 'inactive') {
        videoRecorder.stop();
      }

      // Cleanup audio recording
      if (recordingTimer) {
        clearInterval(recordingTimer);
      }
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
        if (mediaRecorder.stream) {
          mediaRecorder.stream.getTracks().forEach(track => track.stop());
        }
      }
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }
      if (audioContext) {
        audioContext.close();
      }
      if (recordedAudio && recordedAudio.url) {
        URL.revokeObjectURL(recordedAudio.url);
      }
    };
  }, [recordingTimer, mediaRecorder, recordedAudio, audioContext, audioStream, cameraStream, videoRecorder, memoizedChats, unsubscribe]);

  // Get initial values for appointment modal
  const [initialAppointmentValues, setInitialAppointmentValues] = useState({ initialVet: null, initialPetOwner: null, initialPet: null });
  const lastProcessedRef = useRef({ participantId: null, vetId: null });

  useEffect(() => {
    const participantId = selectedChatData?.otherParticipant?.id ?? null;
    const currentUser = authUtils.getCurrentUser();

    // Find the vet that matches the current user
    let initialVet = null;
    let vetId = null;
    if (currentUser && clinicMembers) {
      initialVet = clinicMembers.find(member =>
        member.role === 'doctor' &&
        (member.user_id === currentUser.id || member.user?.id === currentUser.id)
      );
      vetId = initialVet?.user_id ?? initialVet?.user?.id ?? initialVet?.id ?? null;
    }

    // Only update if IDs actually changed
    if (lastProcessedRef.current.participantId !== participantId ||
      lastProcessedRef.current.vetId !== vetId) {

      if (!selectedChatData || !selectedChatData.otherParticipant) {
        setInitialAppointmentValues({ initialVet: null, initialPetOwner: null, initialPet: null });
        lastProcessedRef.current = { participantId: null, vetId: null };
        return;
      }

      // Get pet owner from otherParticipant
      const initialPetOwner = selectedChatData.otherParticipant ? {
        id: selectedChatData.otherParticipant.id,
        first_name: selectedChatData.otherParticipant.first_name,
        last_name: selectedChatData.otherParticipant.last_name,
        full_name: `${selectedChatData.otherParticipant.first_name || ''} ${selectedChatData.otherParticipant.last_name || ''}`.trim(),
        display_name: `${selectedChatData.otherParticipant.first_name || ''} ${selectedChatData.otherParticipant.last_name || ''}`.trim(),
      } : null;

      // Check if this is an SOS conversation and fetch pet information
      const fetchPetFromSOS = async () => {
        // Check if conversation has case_id or case_type indicating it's an SOS conversation
        const caseId = selectedChatData.case_id || selectedChatData.caseId;
        const caseType = selectedChatData.case_type || selectedChatData.caseType;

        if (caseId && (caseType === 'UrgentMedicalCase' || selectedChatData.type === 'medical' || selectedChatData.isSOS)) {
          try {
            const response = await urgentCaseAPI.getUrgentCase(caseId);
            const caseData = response.data?.data || response.data;

            // Get the first pet from the urgent case
            if (caseData?.pets && caseData.pets.length > 0) {
              const firstPet = caseData.pets[0];
              setInitialAppointmentValues({
                initialVet,
                initialPetOwner,
                initialPet: {
                  id: firstPet.id,
                  name: firstPet.name,
                  species: firstPet.species,
                  breed: firstPet.breed,
                  ...firstPet
                }
              });
              lastProcessedRef.current = { participantId, vetId };
              return;
            }
          } catch (error) {
            // Continue to fallback logic below
          }
        } else if (selectedChatData.type === 'medical') {
          // Medical conversation without case_id - fetch guardian's pets and auto-select first one
          try {
            const response = await petAPI.getOtherPets(initialPetOwner.id);
            const pets = response.data?.data || [];

            if (pets.length > 0) {
              const firstPet = pets[0];
              setInitialAppointmentValues({
                initialVet,
                initialPetOwner,
                initialPet: {
                  id: firstPet.id,
                  name: firstPet.name,
                  species: firstPet.species,
                  breed: firstPet.breed,
                  ...firstPet
                }
              });
              lastProcessedRef.current = { participantId, vetId };
              return;
            }
          } catch (error) {
            // Continue to fallback - user can select manually
          }
        }

        // If not SOS or no pet found, set without pet
        setInitialAppointmentValues({ initialVet, initialPetOwner, initialPet: null });
        lastProcessedRef.current = { participantId, vetId };
      };

      fetchPetFromSOS();
    }
  }, [selectedChatData?.otherParticipant?.id, selectedChatData?.case_id, selectedChatData?.case_type, clinicMembers]);

  // Handle opening appointment modal
  const handleOpenAppointmentModal = () => {
    if (!selectedChatData || !selectedChatData.otherParticipant) {
      showErrorToast(t('chat.appointment.noParticipant') || 'Unable to open appointment modal: No participant found');
      return;
    }

    // Check if user has multiple clinics
    if (ownedClinics && ownedClinics.length > 1) {
      setShowSelectClinicModal(true);
    } else if (ownedClinics && ownedClinics.length === 1) {
      // If only one clinic, fetch its setup data to ensure it's fresh
      // This ensures backend updates (like appointment types) are reflected
      handleClinicSelected(ownedClinics[0]);
    } else {
      setShowAppointmentModal(true);
    }
  }

  // Handle clinic selection
  const handleClinicSelected = async (selectedClinic) => {
    setShowSelectClinicModal(false);

    try {
      // Show loading equivalent (using saving state for now to block UI or just proceed)
      setIsSavingAppointment(true);

      const response = await clinicAPI.getClinicSetupData({ clinic_id: selectedClinic.id });
      const responseData = clinicService.transformClinicSetupResponse(response);

      setTargetClinicData(responseData);
      setShowAppointmentModal(true);
    } catch (error) {
      console.error("Error fetching clinic data:", error);
      showErrorToast(t('errors.generic') || 'Failed to load clinic data');
    } finally {
      setIsSavingAppointment(false);
    }
  }

  // Handle saving appointment
  const handleSaveAppointment = async (appointment) => {
    if (!selectedChatData || !selectedChatData.otherParticipant) {
      showErrorToast(t('chat.appointment.noParticipant') || 'Unable to save appointment: No participant found');
      return false;
    }

    const activeClinic = targetClinicData?.clinic || clinic;

    if (!activeClinic || !activeClinic.id) {
      showErrorToast(t('chat.appointment.noClinic') || 'Unable to save appointment: No clinic selected');
      return false;
    }

    try {
      setIsSavingAppointment(true);

      // Validate required data
      if (!appointment.pet_id) {
        showErrorToast(t('receptionAppointments.toasts.petRequired') || 'Pet is required');
        return false;
      }
      if (!appointment.vet_id) {
        showErrorToast(t('receptionAppointments.toasts.vetRequired') || 'Veterinarian is required');
        return false;
      }
      if (!appointment.appointment_type_id) {
        showErrorToast(t('receptionAppointments.toasts.typeRequired') || 'Appointment type is required');
        return false;
      }

      // Combine date and time
      const appointment_at =
        appointment.date && appointment.time
          ? `${appointment.date} ${appointment.time}:00`
          : null;

      if (!appointment_at) {
        showErrorToast(t('receptionAppointments.toasts.datetimeRequired') || 'Date and time are required');
        return false;
      }

      const payload = {
        pet_id: appointment.pet_id,
        clinic_id: activeClinic.id,
        vet_id: appointment.vet_id,
        appointment_at: appointment_at,
        pet_owner_id: selectedChatData.otherParticipant.id,
        reason: appointment.reason || '',
        appointment_type_id: appointment.appointment_type_id,
        // Include conversation_id if available (for SOS conversations that should auto-close)
        ...(selectedChatData?.id && { conversation_id: selectedChatData.id }),
      };


      // Send to backend
      const response = await clinicAppointmentsAPI.createAppointment(payload);

      if (response.data && response.data.status === 'success') {
        showSuccessToast(t('receptionAppointments.toasts.saveSuccess') || 'Appointment scheduled successfully');
        setShowAppointmentModal(false);

        // If this was created from an SOS chat (has conversation_id), refresh the chat to show closure status
        if (selectedChatData?.id && payload.conversation_id) {
          const conversationId = selectedChatData.id;

          // Optimistically update the current chat to show it's closed immediately
          // This provides instant feedback
          setChats(prev => prev.map(chat =>
            chat.id === conversationId
              ? {
                ...chat,
                isClosed: true,
                closedAt: new Date().toISOString(),
                lastMessage: 'Conversation closed - Appointment scheduled'
              }
              : chat
          ));

          // Update chat updates state as well
          setChatUpdates(prev => ({
            ...prev,
            [conversationId]: {
              ...prev[conversationId],
              isClosed: true,
              closedAt: new Date().toISOString(),
              lastMessage: 'Conversation closed - Appointment scheduled'
            }
          }));

          // Refresh the conversation from API to get accurate closure status
          // Use a small delay to ensure backend has processed the closure
          setTimeout(async () => {
            try {
              const conversationResponse = await conversationsAPI.getConversation(conversationId);
              if (conversationResponse?.data?.data) {
                const updatedChat = transformChatData(conversationResponse.data.data);
                if (updatedChat) {
                  setChats(prev => prev.map(chat =>
                    chat.id === conversationId ? updatedChat : chat
                  ));
                }
              }
            } catch (err) {
              console.error('Error refreshing conversation after appointment creation:', err);
              // Fallback to reloading all chats if single conversation refresh fails
              loadChats();
            }
          }, 500);
        }

        return true;
      } else {
        throw new Error(response.data?.message || 'Failed to save appointment');
      }
    } catch (error) {
      console.error('❌ Error saving appointment:', error);
      showErrorToast(error.response?.data?.message || error.message || t('receptionAppointments.toasts.saveFailed') || 'Failed to save appointment');
      return false;
    } finally {
      setIsSavingAppointment(false);
    }
  }

  // Handle ending conversation - show modal
  const handleEndConversation = () => {
    if (!selectedChat) {
      return;
    }

    // Check if conversation is already closed
    if (selectedChatData && selectedChatData.isClosed) {
      if (window.showErrorToast) {
        window.showErrorToast('This conversation is already closed');
      }
      return;
    }

    // Show confirmation modal
    setShowEndConversationModal(true);
  }

  // Actually end the conversation (called from modal)
  // Handle hiding a conversation
  const handleHideChat = async () => {
    if (!selectedChat) return;

    try {
      await conversationsAPI.hideConversation(selectedChat);

      // Update local state to remove the hidden chat
      setChats(prev => prev.filter(c => c.id !== selectedChat));
      // Also update memoized chats if applicable, or rely on loadChats
      setSelectedChat(null);
      setShowChatMenu(false);

      if (window.showSuccessToast) {
        window.showSuccessToast(t("chat.actions.hidden", { defaultValue: "Conversation hidden successfully" }));
      }

      // Refresh chats list to ensure sync
      loadChats();

    } catch (error) {
      console.error("Error hiding chat:", error);
      if (window.showErrorToast) {
        window.showErrorToast(t("chat.errors.hideFailed", { defaultValue: "Failed to hide conversation" }));
      }
    }
  };

  const confirmEndConversation = async () => {
    setShowEndConversationModal(false);

    // IMMEDIATELY update UI to show conversation as closed (optimistic update)

    // Dispatch conversation closed event for instant UI update
    const conversationClosedEvent = new CustomEvent('conversationClosed', {
      detail: {
        conversationId: selectedChat,
        message: 'Conversation ended by you',
        closedBy: 'You',
        timestamp: new Date().toISOString()
      }
    });

    // Dispatch the event IMMEDIATELY for instant UI update
    window.dispatchEvent(conversationClosedEvent);


    try {

      // Call the API to close the conversation
      const response = await conversationsAPI.closeConversation(selectedChat);

      if (response.status === 200 || response.status === 204) {

        // Show success message
        if (window.showSuccessToast) {
          window.showSuccessToast('Conversation ended successfully');
        }

        // If it's an urgent case, show the rating modal after closure
        if (currentChat && (currentChat.urgent_case_id || currentChat.case_id || currentChat.type === 'medical')) {
          const caseId = currentChat.urgent_case_id || currentChat.case_id || selectedChat;
          setRatingCaseId(caseId);
          // Small delay to let the closure toast be seen
          setTimeout(() => {
            setShowRatingModal(true);
          }, 1000);
        }

        // UI is already updated optimistically, so no additional UI changes needed

      } else {
        if (window.showErrorToast) {
          window.showErrorToast('Failed to end conversation');
        }
      }

    } catch (error) {
      console.error('❌ Error ending conversation:', error);

      // Show error message
      if (window.showErrorToast) {
        window.showErrorToast('Failed to end conversation. Please try again.');
      }
    }
  }

  // Cancel ending conversation
  const cancelEndConversation = () => {
    setShowEndConversationModal(false);
  }

  // Use ref for message input to prevent re-renders while typing
  const messageInputRef = useRef(null);

  // Memoize send button disabled state
  const isSendDisabled = useMemo(() => {
    // Prevent sending if chat privacy restricted
    if (currentChat && currentChat.can_send_message === false) return true;

    // Allow sending if there are files selected (even without text) or if there's text
    return (!messageInputText.trim() && selectedFiles.length === 0) || isSending;
  }, [messageInputText, selectedFiles.length, isSending, currentChat?.can_send_message]);

  // Memoize send button styles to prevent re-renders while typing
  const sendButtonStyles = useMemo(() => {
    const canSend = (messageInputText.trim() || selectedFiles.length > 0) && !isSending;
    return {
      padding: "8px",
      backgroundColor: canSend ? "#4A90E2" : "#E0E0E0",
      border: "none",
      borderRadius: "50%",
      cursor: canSend ? "pointer" : "not-allowed",
      flexShrink: 0,
      transition: "background-color 0.2s",
      width: "36px",
      height: "36px",
      minWidth: "36px", // Ensure minimum width
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      visibility: "visible", // Always visible
      opacity: 1, // Always visible
    };
  }, [messageInputText, selectedFiles.length, isSending]);

  // Track if messages have been initially loaded for each conversation
  const initialLoadRef = useRef(new Set());

  // Clear initial load tracking when conversation changes
  useEffect(() => {
    if (selectedChat) {
      // Reset initial load tracking for new conversation
      initialLoadRef.current.delete(selectedChat);
    }
  }, [selectedChat]);

  // Auto-scroll to bottom when messages are loaded (only when first opening conversation)
  useEffect(() => {
    if (selectedChat && !messagesLoading && currentMessages.length > 0) {
      // Only scroll if this is the first time loading messages for this conversation
      if (!initialLoadRef.current.has(selectedChat)) {
        initialLoadRef.current.add(selectedChat);
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          scrollToBottom();
        }, 50);
      }
    }
  }, [selectedChat, messagesLoading]); // Removed memoizedMessages.length to prevent re-renders while typing




  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
          fontFamily: '"Red Hat Display", sans-serif',
        }}
      >
        {/* Navbar */}
        <Navbar />

        {/* Mobile Overlay */}
        {isMobileSidebarOpen && (
          <div
            className="clinic-chat-overlay active"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Chat Container */}
        <div
          className="clinic-chat-container"
          style={{
            paddingTop: "15px",
            maxHeight: '90%',
            height: '90%',
            display: "flex",
            overflow: "hidden",
          }}
        >
          {/* Chat List Sidebar */}
          <div
            className={`clinic-chat-sidebar ${isMobileSidebarOpen ? 'open' : ''}`}
            style={{
              width: "400px",
              backgroundColor: "#FFFFFF",
              borderRight: "1px solid #E0E0E0",
              display: "flex",
              flexDirection: "column",
              flexShrink: 0,
            }}
          >
            {/* Search Header */}
            <div
              className="doctorchat-search-header"
              style={{
                padding: "20px",
                backgroundColor: "#FFFFFF",
                borderBottom: "1px solid #E0E0E0",
                flexShrink: 0,
                position: "relative",
              }}
            >
              {/* Mobile Close Button */}
              <button
                className="mobile-sidebar-close"
                onClick={() => setIsMobileSidebarOpen(false)}
                style={{
                  display: "none", // Hidden by default, shown via CSS on mobile
                  position: "absolute",
                  top: "15px",
                  right: "15px",
                  width: "32px",
                  height: "32px",
                  minWidth: "44px",
                  minHeight: "44px",
                  background: "transparent",
                  border: "1px solid #E0E0E0",
                  borderRadius: "50%",
                  cursor: "pointer",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  color: "#666666",
                  zIndex: 11,
                }}
                aria-label={t("chat.search.closeList", { defaultValue: "Close chat list" })}
              >
                ✕
              </button>
              <div style={{ position: "relative", marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ position: "relative", flex: 1 }}>
                  <Search
                    style={{
                      position: "absolute",
                      right: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#999999",
                      width: "20px",
                      height: "20px",
                      zIndex: 1,
                    }}
                  />
                  <input
                    type="text"
                    className="doctorchat-search-input"
                    placeholder={t("chat.search.placeholder")}
                    style={{
                      width: "100%",
                      padding: "12px 50px 12px 16px",
                      border: "2px solid #E0E0E0",
                      borderRadius: "25px",
                      outline: "none",
                      backgroundColor: "#FFFFFF",
                      fontSize: "14px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      boxSizing: "border-box",
                      transition: "border-color 0.2s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#4A90E2";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#E0E0E0";
                    }}
                  />
                </div>
                <button
                  onClick={loadChats}
                  disabled={loading}
                  className="doctorchat-refresh-btn"
                  style={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: loading ? "#E0E0E0" : "#4A90E2",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    cursor: loading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 0.2s ease",
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = "#357ABD";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = "#4A90E2";
                    }
                  }}
                  title={loading ? t("chat.common.loading") : t("chat.search.refresh")}
                >
                  <RefreshCw
                    size={18}
                    style={{
                      animation: loading ? "spin 1s linear infinite" : "none"
                    }}
                  />
                </button>
              </div>


            </div>

            {/* Chat List */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
              }}
            >
              {loading && (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <div style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #4A90E2",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 10px auto"
                  }}></div>
                  <p>{t("chat.list.loading")}</p>
                  <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
                </div>
              )}
              {error && (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <p style={{ color: "red", marginBottom: "10px" }}>{error}</p>
                  <button
                    onClick={loadChats}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#4A90E2",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer"
                    }}
                  >
                    {t("chat.list.retry")}
                  </button>
                </div>
              )}
              {!loading && !error && chats.length === 0 && (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <div style={{
                    fontSize: "48px",
                    marginBottom: "16px"
                  }}>🏥</div>
                  <p style={{
                    fontSize: "14px",
                    color: "#666666",
                    marginBottom: "8px"
                  }}>
                    {t("chat.medicalEmpty.title", { defaultValue: "No medical consultations found" })}
                  </p>
                  <p style={{
                    fontSize: "12px",
                    color: "#999999"
                  }}>
                    {t("chat.medicalEmpty.subtitle", { defaultValue: "Start a new medical consultation with a veterinarian" })}
                  </p>
                </div>
              )}
              {!loading && !error && chats.map((chat) => chat.isempty === false && (
                <div
                  key={chat.id}
                  className={`chat-item doctorchat-list-item ${recentlyUpdatedChats.has(chat.id) ? 'recently-updated' : ''}`}
                  onClick={() => {

                    setSelectedChat(chat.id);
                    setIsMobileSidebarOpen(false); // Close sidebar on mobile when chat is selected

                    setTimeout(() => {
                      // scrollToBottom();
                    }, 0);
                    // Mark as read will be called automatically by useEffect when selectedChat changes
                  }}
                  style={{
                    padding: "16px 20px",
                    cursor: "pointer",
                    backgroundColor: selectedChat === chat.id ? "#F0F8FF" : "transparent",
                    borderBottom: "1px solid #F0F0F0",
                    borderLeft: "4px solid transparent",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedChat !== chat.id) {


                      e.target.style.backgroundColor = "#FAFAFA"
                      e.target.style.transform = "translateX(2px)"
                    } else if (selectedChat === chat.id) {
                      // Keep medical styling even when selected
                      e.target.style.backgroundColor = "#F0F8FF"
                      e.target.style.borderLeft = "4px solid #059669"
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedChat !== chat.id) {

                      e.target.style.backgroundColor = "transparent"
                      e.target.style.transform = "translateX(0px)"
                    } else if (selectedChat === chat.id) {
                      // Keep medical styling even when selected
                      e.target.style.backgroundColor = "#F0F8FF"
                    }
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <CachedImage
                        src={chat.avatar || "/images/default.jpg"}
                        alt={chat.name}
                        className="doctorchat-list-avatar"
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                      {chat.isOnline && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: "2px",
                            right: "2px",
                            width: "12px",
                            height: "12px",
                            backgroundColor: "#4CAF50",
                            border: "2px solid #FFFFFF",
                            borderRadius: "50%",
                          }}
                        />
                      )}

                    </div>
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <h3
                          className="doctorchat-list-name"
                          style={{
                            fontWeight: 600,
                            color: "#333333",
                            fontSize: "14px",
                            margin: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chat.name}
                        </h3>
                        <span
                          className="doctorchat-list-timestamp"
                          style={{
                            fontSize: "12px",
                            color: "#999999",
                            flexShrink: 0,
                            marginLeft: "8px",
                          }}
                        >
                          {chat.timestamp}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        {(() => {
                          const typingUsers = getTypingUsersForConversation(chat.id);
                          const isTyping = typingUsers.length > 0;

                          return isTyping ? (
                            <p className="chat-item-message doctorchat-list-message typing-indicator" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', fontStyle: 'italic', color: '#666' }}>
                              {t("chat.typing", { defaultValue: "typing..." })}
                            </p>
                          ) : (
                            <p className="chat-item-message doctorchat-list-message" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                              {(() => {
                                const raw = chat.lastMessage;
                                if (raw === null || raw === undefined) return t("chat.list.noMessages");
                                const s = String(raw);
                                const k = s.trim().toLowerCase();
                                if (k === "voice message" || k === "voice message.") return t("chat.messages.voiceMessage");
                                if (!s.trim()) return t("chat.list.noMessages");
                                return s;
                              })()}
                            </p>
                          );
                        })()}
                        {chat.unreadCount > 0 && (
                          <span
                            style={{
                              backgroundColor: "#4A90E2",
                              color: "#FFFFFF",
                              fontSize: "12px",
                              borderRadius: "50%",
                              padding: "2px 6px",
                              minWidth: "18px",
                              height: "18px",
                              textAlign: "center",
                              flexShrink: 0,
                              marginLeft: "8px",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {chat.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chat Window */}
          <div
            className="clinic-chat-main"
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              backgroundColor: "#FFFFFF",
              position: "relative",
            }}
          >
            {/* Mobile Menu Toggle Button - Always visible on mobile */}
            <button
              className="mobile-chat-toggle"
              onClick={() => setIsMobileSidebarOpen(true)}
              style={{
                position: "absolute",
                top: "15px",
                left: "15px",
                width: "40px",
                height: "40px",
                minWidth: "44px",
                minHeight: "44px",
                background: "#8548A8",
                color: "white",
                border: "none",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                cursor: "pointer",
                zIndex: "10",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              }}
              aria-label="Open chat list"
            >
              ☰
            </button>

            {currentChat ? (
              <>

                {/* Chat Header */}
                <div
                  className="chat-header doctorchat-header"
                  style={{
                    padding: "10px 15px",
                    borderBottom: "1px solid #E0E0E0",
                    backgroundColor: "#FFFFFF",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div style={{ position: "relative" }}>
                        <CachedImage
                          src={currentChat.avatar || "/images/default.jpg"}
                          alt={currentChat.name}
                          className="doctorchat-header-avatar"
                          style={{
                            width: "45px",
                            height: "45px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                        {currentChat.isOnline && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "2px",
                              right: "2px",
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#4CAF50",
                              border: "2px solid #FFFFFF",
                              borderRadius: "50%",
                            }}
                          />
                        )}
                      </div>
                      <div>
                        <h2>{currentChat.name}</h2>
                        <p className={`chat-header-status ${currentChat.isClosed ? 'closed' : (currentChat.isOnline ? 'online' : '')}`} style={{ textAlign: isRTL ? 'right' : 'left' }}>
                          {currentChat.isClosed
                            ? t('chat.header.status.closed', { date: formatTimestamp(currentChat.closedAt) })
                            : usersTyping.length > 0
                              ? t('chat.header.status.typing', { defaultValue: 'typing...' })
                              : currentChat.isOnline
                                ? t('chat.header.status.online')
                                : currentChat.lastSeenAt
                                  ? currentChat.lastSeenAt
                                  : t('chat.header.status.recent')}
                        </p>
                      </div>
                    </div>
                    <div
                      className="doctorchat-header-actions"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <button
                        className="doctorchat-header-action-btn"
                        style={{
                          padding: "8px",
                          backgroundColor: "transparent",
                          border: "none",
                          borderRadius: "50%",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => (e.target.style.backgroundColor = "#F5F5F5")}
                        onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                      >
                        <Video style={{ width: "20px", height: "20px", color: "#666666" }} />
                      </button>
                      <button
                        className="doctorchat-header-action-btn"
                        style={{
                          padding: "8px",
                          backgroundColor: "transparent",
                          border: "none",
                          borderRadius: "50%",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => (e.target.style.backgroundColor = "#F5F5F5")}
                        onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                      >
                        <Phone style={{ width: "20px", height: "20px", color: "#666666" }} />
                      </button>

                      {/* Optional Rating Button for SOS cases */}
                      {currentChat.isClosed && (currentChat.urgent_case_id || currentChat.case_id || currentChat.type === 'medical') && (
                        <button
                          className="doctorchat-header-action-btn rating-btn"
                          onClick={() => {
                            const caseId = currentChat.urgent_case_id || currentChat.case_id || selectedChat;
                            setRatingCaseId(caseId);
                            setShowRatingModal(true);
                          }}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#FFF8E1",
                            border: "1px solid #FFD54F",
                            borderRadius: "16px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.2s ease",
                            color: "#F57C00",
                            fontSize: "12px",
                            fontWeight: "600"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#FFECB3";
                            e.currentTarget.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#FFF8E1";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        >
                          <Star style={{ width: "14px", height: "14px", fill: "#F57C00" }} />
                          {t('chat.actions.rate', { defaultValue: 'Rate' })}
                        </button>
                      )}

                      <div style={{ position: 'relative' }} ref={chatMenuRef}>
                        <button
                          className="doctorchat-header-action-btn"
                          onClick={() => setShowChatMenu(!showChatMenu)}
                          style={{
                            padding: "8px",
                            backgroundColor: showChatMenu ? "#E6F0FA" : "transparent",
                            border: "none",
                            borderRadius: "50%",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                          }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = "#F5F5F5")}
                          onMouseLeave={(e) => {
                            if (!showChatMenu) e.target.style.backgroundColor = "transparent"
                          }}
                        >
                          <MoreHorizontal style={{ width: "20px", height: "20px", color: "#666666" }} />
                        </button>

                        {/* Dropdown Menu */}
                        {showChatMenu && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '8px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            padding: '8px 0',
                            minWidth: '160px',
                            zIndex: 100,
                            border: '1px solid #E5E7EB'
                          }}>
                            <button
                              onClick={handleHideChat}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                padding: '8px 16px',
                                border: 'none',
                                backgroundColor: 'transparent',
                                color: '#4B5563',
                                fontSize: '14px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                textAlign: 'left',
                                gap: '8px'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                <line x1="1" y1="1" x2="23" y2="23"></line>
                              </svg>
                              {t("chat.actions.hide", { defaultValue: "Hide Chat" })}
                            </button>

                            {/* You can add more options here like Delete etc in future */}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Messages Area */}
                <div
                  ref={messagesContainerRef}
                  className="doctorchat-messages-container"
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "20px 30px",
                    backgroundColor: "#FAFAFA",
                    position: "relative",
                    // Optimize scrolling performance
                    willChange: "scroll-position",
                    transform: "translateZ(0)", // Force hardware acceleration
                  }}

                >
                  {messagesLoading && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <div style={{
                        width: "30px",
                        height: "30px",
                        border: "3px solid #f3f3f3",
                        borderTop: "3px solid #4A90E2",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        margin: "0 auto 10px auto"
                      }}></div>
                      <p style={{ fontSize: "14px", color: "#666" }}>{t("chat.messages.loading")}</p>
                    </div>
                  )}
                  {messagesError && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <p style={{ color: "red", marginBottom: "10px", fontSize: "14px" }}>{messagesError}</p>
                      <button
                        onClick={() => loadMessages(selectedChat)}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#4A90E2",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px"
                        }}
                      >
                        {t("chat.list.retry")}
                      </button>
                    </div>
                  )}
                  {!messagesLoading && !messagesError && (
                    <div>
                      {/* Load More Messages Button - Show at top if there are more messages */}
                      {hasMoreMessages[selectedChat] && !loadingOlderMessages && memoizedMessageList.length > 0 && (
                        <div style={{
                          textAlign: "center",
                          padding: "15px 20px",
                          borderBottom: "1px solid #E5E5E5",
                          backgroundColor: "#F8F9FA"
                        }}>
                          <button
                            onClick={loadOlderMessages}
                            disabled={loadingOlderMessages}
                            style={{
                              padding: "10px 20px",
                              backgroundColor: loadingOlderMessages ? "#CCCCCC" : "#4A90E2",
                              color: "white",
                              border: "none",
                              borderRadius: "20px",
                              cursor: loadingOlderMessages ? "not-allowed" : "pointer",
                              fontSize: "14px",
                              fontWeight: "500",
                              fontFamily: '"Red Hat Display", sans-serif',
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              margin: "0 auto",
                              transition: "all 0.2s ease",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                            }}
                            onMouseEnter={(e) => {
                              if (!loadingOlderMessages) {
                                e.target.style.backgroundColor = "#357ABD";
                                e.target.style.transform = "translateY(-1px)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!loadingOlderMessages) {
                                e.target.style.backgroundColor = "#4A90E2";
                                e.target.style.transform = "translateY(0)";
                              }
                            }}
                          >
                            {loadingOlderMessages ? (
                              <>
                                <div style={{
                                  width: "16px",
                                  height: "16px",
                                  border: "2px solid #ffffff",
                                  borderTop: "2px solid transparent",
                                  borderRadius: "50%",
                                  animation: "spin 1s linear infinite"
                                }} />
                                {t("chat.messages.loadMore.loading")}
                              </>
                            ) : (
                              <>
                                <span>↑</span>
                                {t("chat.messages.loadMore.trigger")}
                              </>
                            )}
                          </button>
                        </div>
                      )}

                      {/* Messages */}
                      {MemoizedMessagesContainer}

                      {/* Typing Indicator */}
                      {usersTyping.length > 0 && (
                        <TypingIndicator />
                      )}



                    </div>
                  )}
                  {/* Scroll to bottom button - only show when there are new messages while scrolled up */}
                  {/* <div ref={messagesEndRef} /> */}
                </div>
                <div className="chat-buttons-container">
                  <div className="chat-buttons">
                    <button
                      class="vet-chat-buttons vet-chat-end-button"
                      onClick={handleEndConversation}
                      disabled={selectedChatData && selectedChatData.isClosed}
                      style={{
                        opacity: selectedChatData && selectedChatData.isClosed ? 0.5 : 1,
                        cursor: selectedChatData && selectedChatData.isClosed ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {selectedChatData && selectedChatData.isClosed ? 'Closed' : 'End'}
                    </button>
                    <button
                      class="vet-chat-buttons vet-chat-schedule-button"
                      onClick={handleOpenAppointmentModal}
                      disabled={!selectedChatData || selectedChatData.isClosed}
                      style={{
                        opacity: (!selectedChatData || selectedChatData.isClosed) ? 0.5 : 1,
                        cursor: (!selectedChatData || selectedChatData.isClosed) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Schedule Appointment
                    </button>
                  </div>
                </div>

                {/* Closed Conversation Message */}
                {selectedChatData && selectedChatData.isClosed && (
                  <div className="doctorchat-closed-message" style={{
                    padding: "20px 30px",
                    backgroundColor: "#f8f9fa",
                    borderTop: "1px solid #e9ecef",
                    textAlign: "center"
                  }}>
                    <div style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "12px 20px",
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffeaa7",
                      borderRadius: "8px",
                      color: "#856404"
                    }}>
                      <span style={{ fontSize: "16px" }}>🔒</span>
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>
                        This conversation has been closed
                      </span>
                    </div>
                  </div>
                )}

                {/* Message Input - Hide when conversation is closed */}
                {selectedChatData && !selectedChatData.isClosed && (
                  <div
                    className="doctorchat-input-container"
                    style={{
                      paddingBottom: "20px",
                      padding: "0 20px 20px 20px",
                      backgroundColor: "#FFFFFF",
                      flexShrink: 0,
                    }}
                  >
                    {/* Recording Indicator */}
                    {isRecording && (
                      <div className="chat-recording-indicator">
                        <div className="chat-recording-content">
                          <div className="chat-recording-animation">
                            <div className="chat-recording-dot"></div>
                          </div>
                          <div className="chat-recording-waveform">
                            <AudioWaveform
                              audioContext={audioContext}
                              analyserNode={analyserNode}
                              isRecording={isRecording}
                              width={200}
                              height={40}
                              color="#EF4444"
                            />
                          </div>
                          <div className="chat-recording-info">
                            <span className="chat-recording-text">{t("chat.recording.recording")}</span>
                            <span className="chat-recording-duration">{formatDuration(recordingDuration)}</span>
                          </div>
                          <button
                            onClick={cancelRecording}
                            className="chat-recording-cancel-btn"
                            title={t("chat.recording.cancel")}
                          >
                            ✕
                          </button>
                          <button
                            onClick={stopRecording}
                            className="chat-recording-stop-btn"
                            title={t("chat.recording.stopReview")}
                          >
                            {t("chat.recording.stop")}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Recorded Audio Preview */}
                    {recordedAudio && !isRecording && (
                      <div className="chat-audio-preview">
                        <div className="chat-audio-preview-content">
                          <div className="chat-audio-preview-info">
                            <div className="chat-audio-preview-icon">🎤</div>
                            <div>
                              <p className="chat-audio-preview-title">{t("chat.messages.voiceMessage")}</p>
                              <p className="chat-audio-preview-duration">{formatDuration(recordedAudio.duration)}</p>
                            </div>
                          </div>
                          <audio
                            src={recordedAudio.url}
                            controls
                            muted={false}
                            className="chat-audio-preview-player"
                            onLoadedMetadata={(e) => {
                              const audio = e.target;
                              audio.muted = false;
                              audio.volume = 1.0;
                            }}
                            onCanPlay={(e) => {
                              const audio = e.target;
                              audio.muted = false;
                              audio.volume = 1.0;
                            }}
                            onPlay={(e) => {
                              const audio = e.target;
                              audio.muted = false;
                              audio.volume = 1.0;
                            }}
                            onVolumeChange={(e) => {
                              const audio = e.target;
                              if (audio.muted) {
                                audio.muted = false;
                              }
                              if (audio.volume < 1.0) {
                                audio.volume = 1.0;
                              }
                            }}
                          />
                          <div className="chat-audio-preview-actions">
                            <button
                              onClick={cancelRecording}
                              className="chat-audio-preview-cancel"
                              title={t("chat.audioPreview.cancel")}
                            >
                              ✕
                            </button>
                            <button
                              onClick={sendRecordedAudio}
                              disabled={isSending}
                              className="chat-audio-preview-send"
                              title={t("chat.audioPreview.send")}
                            >
                              {isSending ? (
                                <div className="chat-input-send-spinner" />
                              ) : (
                                <Send className="chat-input-send-icon" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Selected Files Preview */}
                    {/* Selected Files Preview */}
                    {selectedFiles.length > 0 && (
                      <div
                        className="doctorchat-files-preview"
                        style={{
                          marginBottom: "12px",
                          padding: "12px",
                          backgroundColor: "#F8F9FA",
                          borderRadius: "8px",
                          border: "1px solid #E9ECEF",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "12px", fontWeight: "500", color: "#495057" }}>
                              Preview ({selectedFiles.length} files)
                            </span>
                            <span style={{
                              fontSize: "10px",
                              color: "#6C757D",
                              backgroundColor: "#E9ECEF",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontStyle: "italic"
                            }}>
                              Text input disabled
                            </span>
                          </div>
                          <button
                            onClick={() => setSelectedFiles([])}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#6C757D",
                              cursor: "pointer",
                              fontSize: "12px",
                              textDecoration: "underline",
                            }}
                          >
                            Clear all
                          </button>
                        </div>
                        <div className="doctorchat-files-grid" style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                          gap: "12px",
                          maxWidth: "100%",
                          maxHeight: "400px", // Prevent file preview from taking too much vertical space
                          overflowY: "auto" // Add scroll if too many files
                        }}>

                          {selectedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="doctorchat-file-preview"
                              style={{
                                backgroundColor: "#FFFFFF",
                                borderRadius: "8px",
                                border: "1px solid #DEE2E6",
                                overflow: "hidden",
                                minWidth: "0",
                                maxWidth: "100%",
                                maxHeight: "300px" // Prevent individual file previews from being too tall
                              }}
                            >
                              {/* File Header */}
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "8px 12px",
                                backgroundColor: "#F8F9FA",
                                borderBottom: "1px solid #DEE2E6",
                                minWidth: 0
                              }}>
                                <div style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  minWidth: 0,
                                  flex: 1
                                }}>
                                  <span style={{ fontSize: "16px", flexShrink: 0 }}>{getFileIcon(file.type)}</span>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <p style={{
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      margin: "0 0 2px 0",
                                      color: "#495057",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap"
                                    }}>
                                      {file.name}
                                    </p>
                                    <p style={{
                                      fontSize: "10px",
                                      margin: 0,
                                      color: "#6C757D"
                                    }}>
                                      {formatFileSize(file.size)}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => removeFile(index)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: "#DC3545",
                                    cursor: "pointer",
                                    padding: "2px",
                                    fontSize: "12px"
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                              {/* File Content */}
                              {file.type && file.type.startsWith('image/') && (
                                <div style={{ padding: "8px" }}>
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    className="doctorchat-file-preview-image"
                                    loading="lazy"
                                    decoding="async"
                                    style={{
                                      width: "100%",
                                      height: "auto",
                                      maxHeight: "200px",
                                      maxWidth: "250px", // Add max width constraint
                                      objectFit: "cover",
                                      borderRadius: "4px"
                                    }}
                                    onLoad={(e) => {
                                      // Optimize image loading
                                      e.target.style.opacity = "1";
                                    }}
                                    onError={(e) => {
                                      // Handle image load errors gracefully
                                      e.target.style.display = "none";
                                    }}
                                  />
                                </div>
                              )}
                              {file.type && file.type.startsWith('video/') && (
                                <div style={{ padding: "8px" }}>
                                  <video
                                    controls
                                    preload="metadata"
                                    className="doctorchat-file-preview-video"
                                    style={{
                                      width: "100%",
                                      height: "auto",
                                      maxHeight: "200px",
                                      maxWidth: "250px", // Add max width constraint
                                      borderRadius: "4px"
                                    }}
                                  >
                                    <source src={URL.createObjectURL(file)} type={file.type} />
                                    Your browser does not support the video tag.
                                  </video>
                                </div>
                              )}
                              {(!file.type || (!file.type.startsWith('image/') && !file.type.startsWith('video/'))) && (
                                <div style={{ padding: "12px" }}>
                                  <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginBottom: "4px"
                                  }}>
                                    <span style={{ fontSize: "10px", color: "#6C757D" }}>
                                      {formatFileSize(file.size)}
                                    </span>
                                    <span style={{ fontSize: "10px", color: "#6C757D" }}>
                                      {getFileIcon(file.type)}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div
                      className="doctorchat-input-bar"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "8px 12px",
                        border: "1px solid #E0E0E0",
                        borderRadius: "25px",
                        backgroundColor: "#FFFFFF",
                        overflow: "hidden", // Prevent overflow on small screens
                        minWidth: 0, // Allow container to shrink
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar"
                        onChange={handleFileSelect}
                        style={{ display: "none" }}
                      />
                      <button
                        onClick={() => !(currentChat?.can_send_message === false) && fileInputRef.current?.click()}
                        className="doctorchat-input-btn"
                        disabled={currentChat?.can_send_message === false}
                        style={{
                          padding: "4px",
                          backgroundColor: "transparent",
                          border: "none",
                          cursor: currentChat?.can_send_message === false ? "not-allowed" : "pointer",
                          flexShrink: 0,
                        }}
                        title="Attach file"
                      >
                        <Paperclip style={{ width: "18px", height: "18px", color: currentChat?.can_send_message === false ? '#999999' : "#999999" }} />
                      </button>
                      <button
                        onClick={() => !(currentChat?.can_send_message === false) && handleCameraClick()}
                        className="doctorchat-input-btn"
                        disabled={currentChat?.can_send_message === false}
                        style={{
                          padding: "4px",
                          backgroundColor: "transparent",
                          border: "none",
                          cursor: currentChat?.can_send_message === false ? "not-allowed" : "pointer",
                          flexShrink: 0,
                        }}
                        title="Open camera"
                      >
                        <Camera style={{ width: "18px", height: "18px", color: "#999999" }} />
                      </button>
                      <input
                        ref={messageInputRef}
                        type="text"
                        className="doctorchat-input"
                        value={messageInputText}
                        onChange={(e) => {
                          setMessageInputText(e.target.value);
                          onTyping(e);
                        }}
                        onKeyPress={handleKeyPress}
                        placeholder={
                          currentChat?.can_send_message === false
                            ? (t('chat.input.privacyRestricted') || 'You cannot message this user due to their privacy settings')
                            : (selectedFiles.length > 0 ? "Media selected - click send to share" : "Type a message...")
                        }
                        disabled={selectedFiles.length > 0 || currentChat?.can_send_message === false}
                        style={{
                          flex: 1,
                          minWidth: 0, // Allow input to shrink on small screens
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          fontFamily: '"Red Hat Display", sans-serif',
                          backgroundColor: (selectedFiles.length > 0 || currentChat?.can_send_message === false) ? "#F5F5F5" : "transparent",
                          padding: "8px 4px",
                          color: (selectedFiles.length > 0 || currentChat?.can_send_message === false) ? "#999999" : "#333333",
                          cursor: (selectedFiles.length > 0 || currentChat?.can_send_message === false) ? "not-allowed" : "text",
                          ...getRTLStyles(messageInputText)
                        }}
                      />
                      <button
                        onClick={() => !(currentChat?.can_send_message === false) && setShowEmojiPicker(!showEmojiPicker)}
                        className="doctorchat-input-btn"
                        disabled={currentChat?.can_send_message === false}
                        style={{
                          padding: "4px",
                          backgroundColor: "transparent",
                          border: "none",
                          cursor: currentChat?.can_send_message === false ? "not-allowed" : "pointer",
                          flexShrink: 0,
                        }}
                        title="Emoji"
                      >
                        <Smile style={{ width: "18px", height: "18px", color: currentChat?.can_send_message === false ? '#999999' : "#999999" }} />
                      </button>
                      <button
                        onClick={() => {
                          if (isRecording) {
                            stopRecording();
                          } else if (recordedAudio) {
                            // If there's a recorded audio, send it
                            sendRecordedAudio();
                          } else {
                            // Start new recording
                            startRecording();
                          }
                        }}
                        disabled={selectedFiles.length > 0 || currentChat?.can_send_message === false}
                        className="doctorchat-input-btn"
                        style={{
                          padding: "4px",
                          backgroundColor: "transparent",
                          border: "none",
                          cursor: (selectedFiles.length > 0 || currentChat?.can_send_message === false) ? "not-allowed" : "pointer",
                          flexShrink: 0,
                        }}
                        title={isRecording
                          ? t("chat.input.voice.stop")
                          : recordedAudio
                            ? t("chat.input.voice.send")
                            : t("chat.input.voice.start")
                        }
                      >
                        <Mic style={{ width: "18px", height: "18px", color: currentChat?.can_send_message === false ? '#999999' : micButtonColor }} />
                      </button>
                      <button
                        onClick={handleSendMessage}
                        disabled={isSendDisabled}
                        className="doctorchat-input-btn"
                        style={sendButtonStyles}
                      >
                        {isSending ? (
                          <div style={{
                            width: "16px",
                            height: "16px",
                            border: "2px solid #FFFFFF",
                            borderTop: "2px solid transparent",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite"
                          }} />
                        ) : (
                          <Send style={{ width: "16px", height: "16px", color: "#FFFFFF" }} />
                        )}
                      </button>
                    </div>
                    {showEmojiPicker && (
                      <EmojiPicker
                        onEmojiSelect={insertEmoji}
                        onClose={() => setShowEmojiPicker(false)}
                        positionRef={emojiButtonRef}
                      />
                    )}
                  </div>
                )}
              </>
            ) : conversationLoading ? (
              <div
                className="doctorchat-empty-state"
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#FAFAFA",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div
                    className="doctorchat-empty-icon-container"
                    style={{
                      width: "64px",
                      height: "64px",
                      backgroundColor: "#E5E7EB",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px auto",
                    }}
                  >
                    <div style={{
                      width: "32px",
                      height: "32px",
                      border: "3px solid #f3f3f3",
                      borderTop: "3px solid #4A90E2",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite"
                    }}></div>
                  </div>
                  <h3
                    className="doctorchat-empty-title"
                    style={{
                      fontSize: "18px",
                      fontWeight: 500,
                      color: "#111827",
                      marginBottom: "8px",
                      margin: "0 0 8px 0",
                    }}
                  >
                    Opening conversation...
                  </h3>
                  <p
                    className="doctorchat-empty-text"
                    style={{
                      color: "#6B7280",
                      margin: 0,
                      fontSize: "14px",
                    }}
                  >
                    Please wait while we load the conversation
                  </p>
                </div>
              </div>
            ) : (
              <div
                className="doctorchat-empty-state"
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#FAFAFA",
                  paddingTop: "60px", // Space for mobile toggle button
                }}
              >
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <div
                    className="doctorchat-empty-icon-container"
                    style={{
                      width: "64px",
                      height: "64px",
                      backgroundColor: "#E5E7EB",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px auto",
                    }}
                  >
                    <Search className="doctorchat-empty-icon" style={{ width: "32px", height: "32px", color: "#9CA3AF" }} />
                  </div>
                  <h3
                    className="doctorchat-empty-title"
                    style={{
                      fontSize: "18px",
                      fontWeight: 500,
                      color: "#111827",
                      marginBottom: "8px",
                      margin: "0 0 8px 0",
                    }}
                  >
                    {t("chat.empty.title")}
                  </h3>
                  <p
                    className="doctorchat-empty-text"
                    style={{
                      color: "#6B7280",
                      margin: "0 0 16px 0",
                      fontSize: "14px",
                    }}
                  >
                    {t("chat.empty.subtitle")}
                  </p>
                  {/* Mobile hint */}
                  <p
                    className="doctorchat-mobile-hint"
                    style={{
                      color: "#8548A8",
                      margin: "16px 0 0 0",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    {t("chat.empty.mobileHint", { defaultValue: "👆 Tap the menu button (☰) to view conversations" })}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Camera Modal */}
          {isCameraOpen && (
            <div className="chat-camera-modal" onClick={stopCamera}>
              <div className="chat-camera-modal-content" onClick={(e) => e.stopPropagation()}>
                {/* Close button */}
                <button onClick={stopCamera} className="chat-camera-modal-close">
                  ✕
                </button>

                {/* Camera preview */}
                <div className="chat-camera-preview">
                  {cameraLoading && (
                    <div className="chat-camera-loading">
                      <div className="chat-camera-loading-spinner"></div>
                      <p>{t("chat.camera.starting")}</p>
                    </div>
                  )}
                  <video
                    ref={cameraVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="chat-camera-video"
                    style={{ display: cameraLoading ? 'none' : 'block' }}
                    onLoadedMetadata={(e) => {
                      const video = e.target;
                      setCameraLoading(false);
                      video.play().catch(err => {
                        console.error('Error playing video:', err);
                      });
                    }}
                    onCanPlay={(e) => {
                      const video = e.target;
                      setCameraLoading(false);
                      video.play().catch(err => {
                        console.error('Error playing video:', err);
                      });
                    }}
                    onError={(e) => {
                      console.error('📷 Video error:', e.target.error);
                      setCameraLoading(false);
                    }}
                  />
                  <canvas ref={cameraCanvasRef} style={{ display: 'none' }} />

                  {/* Recording indicator for video mode */}
                  {isRecordingVideo && (
                    <div className="chat-camera-recording-indicator">
                      <div className="chat-camera-recording-dot"></div>
                      <span>{t("chat.camera.recording")}</span>
                    </div>
                  )}
                </div>

                {/* Camera controls */}
                <div className="chat-camera-controls">
                  {/* Mode toggle */}
                  <div className="chat-camera-mode-toggle">
                    <button
                      onClick={() => setCameraMode('photo')}
                      className={`chat-camera-mode-btn ${cameraMode === 'photo' ? 'active' : ''}`}
                    >
                      Photo
                    </button>
                    <button
                      onClick={() => setCameraMode('video')}
                      className={`chat-camera-mode-btn ${cameraMode === 'video' ? 'active' : ''}`}
                    >
                      Video
                    </button>
                  </div>

                  {/* Action buttons */}
                  <div className="chat-camera-actions">
                    {cameraMode === 'photo' ? (
                      <button
                        onClick={capturePhoto}
                        className="chat-camera-capture-btn"
                        title="Capture photo"
                      >
                        <div className="chat-camera-capture-icon"></div>
                      </button>
                    ) : (
                      <>
                        {!isRecordingVideo ? (
                          <button
                            onClick={startVideoRecording}
                            className="chat-camera-record-btn"
                            title={t("chat.camera.startRecording")}
                          >
                            <div className="chat-camera-record-icon"></div>
                          </button>
                        ) : (
                          <button
                            onClick={stopVideoRecording}
                            className="chat-camera-stop-btn"
                            title={t("chat.camera.stopRecording")}
                          >
                            <div className="chat-camera-stop-icon"></div>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Media Modal */}
          {showMediaModal && selectedMedia && (
            <div
              className="doctorchat-media-modal"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: "20px"
              }}
              onClick={closeMediaModal}
            >
              <div
                className="doctorchat-media-modal-content"
                style={{
                  position: "relative",
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  backgroundColor: "transparent"
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  onClick={closeMediaModal}
                  className="doctorchat-media-modal-close"
                  style={{
                    position: "absolute",
                    top: "-40px",
                    right: "0",
                    background: "none",
                    border: "none",
                    color: "white",
                    fontSize: "24px",
                    cursor: "pointer",
                    zIndex: 1001,
                    padding: "8px"
                  }}
                >
                  ✕
                </button>

                {/* Media content */}
                {selectedMedia.isImage || (selectedMedia.type && selectedMedia.type.startsWith('image/')) ? (
                  <CachedImage
                    src={selectedMedia.url}
                    alt={selectedMedia.name || "Media"}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "90vh",
                      objectFit: "contain",
                      borderRadius: "8px"
                    }}
                  />
                ) : selectedMedia.isVideo || (selectedMedia.type && selectedMedia.type.startsWith('video/')) ? (
                  <video
                    controls
                    autoPlay
                    style={{
                      maxWidth: "100%",
                      maxHeight: "90vh",
                      borderRadius: "8px"
                    }}
                  >
                    <source src={selectedMedia.url} type={selectedMedia.type} />
                    Your browser does not support the video tag.
                  </video>
                ) : selectedMedia.isDocument || (selectedMedia.type && selectedMedia.type.startsWith('application/')) ? (
                  <div
                    style={{
                      backgroundColor: "white",
                      padding: "40px",
                      borderRadius: "8px",
                      textAlign: "center",
                      maxWidth: "400px"
                    }}
                  >
                    <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                      {getFileIcon(selectedMedia.type)}
                    </div>
                    <h3 style={{ margin: "0 0 8px 0", fontSize: "18px" }}>
                      {selectedMedia.name || "Document"}
                    </h3>
                    <p style={{ margin: "0 0 16px 0", color: "#666" }}>
                      {formatFileSize(selectedMedia.size || 0)}
                    </p>
                    <a
                      href={selectedMedia.url}
                      download={selectedMedia.name}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-block",
                        padding: "12px 24px",
                        backgroundColor: "#4A90E2",
                        color: "white",
                        textDecoration: "none",
                        borderRadius: "6px",
                        fontWeight: "500"
                      }}
                    >
                      Download File
                    </a>
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundColor: "white",
                      padding: "40px",
                      borderRadius: "8px",
                      textAlign: "center",
                      maxWidth: "400px"
                    }}
                  >
                    <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                      {getFileIcon(selectedMedia.type)}
                    </div>
                    <h3 style={{ margin: "0 0 8px 0", fontSize: "18px" }}>
                      {selectedMedia.name || "File"}
                    </h3>
                    <p style={{ margin: "0 0 16px 0", color: "#666" }}>
                      {formatFileSize(selectedMedia.size || 0)}
                    </p>
                    <a
                      href={selectedMedia.url}
                      download={selectedMedia.name}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-block",
                        padding: "12px 24px",
                        backgroundColor: "#4A90E2",
                        color: "white",
                        textDecoration: "none",
                        borderRadius: "6px",
                        fontWeight: "500"
                      }}
                    >
                      Download File
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* End Conversation Modal */}
          {showEndConversationModal && (
            <div
              className="doctorchat-end-modal"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: "20px"
              }}
              onClick={cancelEndConversation}
            >
              <div
                className="doctorchat-end-modal-content"
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  maxWidth: "400px",
                  width: "100%",
                  boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)"
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div style={{ marginBottom: "16px" }}>
                  <h3 className="doctorchat-end-modal-title" style={{
                    margin: "0 0 8px 0",
                    fontSize: "20px",
                    fontWeight: "600",
                    color: "#1f2937"
                  }}>
                    End Conversation
                  </h3>
                  <p className="doctorchat-end-modal-text" style={{
                    margin: "0",
                    color: "#6b7280",
                    fontSize: "14px",
                    lineHeight: "1.5"
                  }}>
                    Are you sure you want to end this medical consultation?
                  </p>
                </div>

                {/* Warning Message */}
                <div style={{
                  backgroundColor: "#fef3c7",
                  border: "1px solid #f59e0b",
                  borderRadius: "8px",
                  padding: "12px",
                  marginBottom: "20px"
                }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{
                      fontSize: "16px",
                      marginRight: "8px",
                      color: "#f59e0b"
                    }}>
                      ⚠️
                    </div>
                    <p style={{
                      margin: "0",
                      fontSize: "14px",
                      color: "#92400e",
                      fontWeight: "500"
                    }}>
                      This action cannot be undone and will close the current medical consultation.
                    </p>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="doctorchat-end-modal-actions" style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end"
                }}>
                  <button
                    onClick={cancelEndConversation}
                    style={{
                      padding: "10px 20px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      color: "#374151",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = "#f9fafb";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = "white";
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmEndConversation}
                    style={{
                      padding: "10px 20px",
                      border: "none",
                      borderRadius: "8px",
                      backgroundColor: "#ef4444",
                      color: "white",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = "#dc2626";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = "#ef4444";
                    }}
                  >
                    End Conversation
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div >

      <SelectClinicModal
        open={showSelectClinicModal}
        clinics={ownedClinics}
        onSelect={handleClinicSelected}
        preventNavigation={true}
      />

      {/* SOS Rating Modal */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        onRate={({ rating, comment }) => urgentCaseAPI.rateUrgentCase(ratingCaseId, { rating, comment })}
        onSkip={() => setShowRatingModal(false)}
        title={t('ratings.title', { defaultValue: 'Rate Experience' })}
        subtitle={t('ratings.subtitle', { defaultValue: 'How was your experience with this urgent case?' })}
      />

      {/* Appointment Modal */}
      <AppointmentModal
        isOpen={showAppointmentModal}
        onClose={() => {
          setShowAppointmentModal(false);
          setTargetClinicData(null); // Reset target data on close
        }}
        onSave={handleSaveAppointment}
        vets={(targetClinicData?.clinic_members || clinicMembers)?.filter(member => member.role === 'doctor') || []}
        workingHours={(targetClinicData?.clinic?.working_hours || clinic?.working_hours) || []}
        appointmentTypes={(targetClinicData?.appointment_types || clinicData?.appointment_types) || []}
        isAppointments={true}
        initialVet={initialAppointmentValues.initialVet}
        initialPetOwner={initialAppointmentValues.initialPetOwner}
        initialPet={initialAppointmentValues.initialPet}
      />
    </>
  )
}

export default DoctorChat